import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, FileUp, FileQuestion, Trash2, ArrowLeft, Plus, AlertTriangle, DollarSign, Hammer, Gauge, FileSpreadsheet, FileText, ListChecks, FolderKanban, Share2, XCircle, BellRing, Bell, BellOff, CheckCircle2, RefreshCw, FileDiff, Archive, ArchiveRestore, ArrowUpNarrowWide, ArrowDownWideNarrow, Kanban, Bookmark, HardDrive, RotateCcw, Mail, Sparkles, Link2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { ResponseQuestionRow, FLAG_LABELS, DONE_FLAGS as DONE_FLAGS_UI, qualityOf } from '../components/rfp/ResponseQuestionRow';
import { AttachmentAdminDialog } from '../components/rfp/AttachmentAdminDialog';
import { ResponseProgress } from '../components/rfp/ResponseProgress';
import { ResponseScoreCard } from '../components/rfp/ResponseScoreCard';
import { MyQuestionsView } from '../components/rfp/MyQuestionsView';
import { WorkloadView } from '../components/rfp/WorkloadView';
import { ClientScoringPanel } from '../components/rfp/ClientScoringPanel';
import { RfpInsights } from '../components/rfp/RfpInsights';
import { DeadlineCalendar } from '../components/rfp/DeadlineCalendar';
import { PipelineBoard } from '../components/rfp/PipelineBoard';
import { AiJobsChip } from '../components/rfp/AiJobsChip';
import { WinStoriesDialog } from '../components/rfp/WinStoriesDialog';
import { IntakeDropzone, IntakeReview, IntakeSummaryDialog } from '../components/rfp/IntakePanel';
import { ResponseShell } from '../components/rfp/ResponseShell';
import { CommercialsCard } from '../components/rfp/CommercialsCard';
import { PackageCard } from '../components/rfp/PackageCard';
import { NarrativesPanel, AttachmentsPanel } from '../components/rfp/ResponseTabs';
import { GapTasksCard } from '../components/rfp/GapTasksCard';
import { NarrativePickerDialog } from '../components/rfp/NarrativePickerDialog';
import { SubmissionLogCard, SubmissionDueBanner } from '../components/rfp/SubmissionLog';
import { AutoDraftReviewQueue } from '../components/rfp/AutoDraftReviewQueue';
import { ReviewerWorkloadDialog } from '../components/rfp/ReviewerWorkloadDialog';
import { DocScaleDialog } from '../components/rfp/DocScaleDialog';
import { ScaleRemapDialog, docScaleMismatch } from '../components/rfp/ScaleRemapDialog';
import { LibraryWordingDialog } from '../components/rfp/LibraryWordingDialog';
import { StructureEditor } from '../components/rfp/StructureEditor';
import { TemplateSuggestion, ClientFitBars } from '../components/rfp/StructureTools';
import { CalendarDays, Pencil, Star, Users, ListOrdered, History, Eye } from 'lucide-react';
import HistoryPanel from '../components/rfp/HistoryPanel';
import { StepRail } from '../components/rfp/StepRail';
import PreviewPanel from '../components/rfp/PreviewPanel';
import { PreviewShareDialog } from '../components/rfp/PreviewShareDialog';
import { ProjectProfile } from '../components/rfp/ProjectProfile';
import { BuildRecap } from '../components/rfp/BuildRecap';
import { DeletedAnnexures } from '../components/rfp/DeletedAnnexures';
import { useCollapse, allAccepted, isMine } from '../components/rfp/useCollapse';
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, UserRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STRENGTH = {
  strong: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  none: 'bg-rose-50 text-rose-700 border-rose-200',
};
const BUSY = ['extracting', 'matching', 'building'];

const ScorePanel = ({ scores }) => (
  <div className="space-y-2" data-testid="rfp-resp-scores">
    <div className="flex items-center gap-3 flex-wrap text-xs">
      <span className="text-2xl font-bold text-[#0F2040]" data-testid="rfp-resp-overall">{scores.overall}%</span>
      <span className="text-slate-500">match score</span>
      {scores.weighted && (
        <span className="inline-flex items-baseline gap-1 pl-3 border-l border-slate-200" data-testid="rfp-resp-weighted"
          title={`Client-weighted fit — the sheet's category weights applied to our match per category (${Math.round(scores.weighted.coverage * 100)}% of weight covered):\n` + Object.entries(scores.weighted.by_category).map(([c, r]) => `${c}: ${r.score}% × ${Math.round(r.weight * 100)}%`).join('\n')}>
          <span className="text-2xl font-bold text-[#4ABFCF]">{scores.weighted.score}%</span>
          <span className="text-slate-500">client-weighted</span>
        </span>
      )}
      <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">{scores.strong} strong</Badge>
      <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">{scores.partial} partial</Badge>
      <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">{scores.gaps} gaps</Badge>
      {scores.pricing_flagged > 0 && (
        <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-200">
          <DollarSign className="w-2.5 h-2.5 mr-0.5" />{scores.pricing_flagged} pricing flagged
        </Badge>
      )}
    </div>
    {Object.entries(scores.by_category || {}).map(([cat, r]) => (
      <div key={cat} className="flex items-center gap-2 text-[10px]">
        <span className="w-40 truncate text-slate-600">{cat}</span>
        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${r.score >= 70 ? 'bg-emerald-500' : r.score >= 40 ? 'bg-amber-500' : 'bg-rose-500'}`}
            style={{ width: `${Math.max(3, r.score)}%` }} />
        </div>
        <span className="w-24 text-right text-slate-500">{r.score}% · {r.total} q{r.gaps ? ` · ${r.gaps} gap${r.gaps > 1 ? 's' : ''}` : ''}</span>
      </div>
    ))}
  </div>
);

const dueBadge = (due) => {
  if (!due) return null;
  const days = Math.ceil((new Date(due + 'T00:00:00') - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (days < 0) return { text: `Overdue ${-days}d`, cls: 'bg-rose-100 text-rose-700 border-rose-300' };
  if (days === 0) return { text: 'Due today', cls: 'bg-rose-50 text-rose-700 border-rose-200' };
  if (days <= 7) return { text: `Due in ${days}d`, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { text: `Due in ${days}d`, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
};
const OUTCOME_CLS = {
  won: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  lost: 'bg-rose-100 text-rose-700 border-rose-300',
};

export default function RfpResponsePage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [project, setProject] = useState(null);
  const [newName, setNewName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [scoring, setScoring] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState('projects');
  const [search, setSearch] = useState('');
  const [fOutcome, setFOutcome] = useState('all');
  const [fOwner, setFOwner] = useState('all');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [sortKey, setSortKey] = useState('due');
  const [sortDir, setSortDir] = useState('asc');
  const [showArchived, setShowArchived] = useState(false);
  const [views, setViews] = useState([]);
  const [fileAdminOpen, setFileAdminOpen] = useState(false);
  const [qfStatus, setQfStatus] = useState('all');
  const [qfAssignee, setQfAssignee] = useState('all');
  const [qfGaps, setQfGaps] = useState(false);
  const [qfWeakest, setQfWeakest] = useState(false);
  const [qfScore, setQfScore] = useState('all');
  const [qfMatch, setQfMatch] = useState('all');
  const [qfConf, setQfConf] = useState('all');
  const [qfFit, setQfFit] = useState('all');
  const [qfSearch, setQfSearch] = useState('');
  const [keptQs, setKeptQs] = useState({});
  const collapse = useCollapse(project?.id);
  useEffect(() => {
    if (!project) return;
    const sp = new URLSearchParams(window.location.search); const dd = sp.get('doc'); if (!dd) return;
    const row = sp.get('row');
    collapse.reveal(dd, row !== null ? ((project.documents || []).find(d => d.id === dd)?.questions || []).find(q => String(q.idx) === row)?.section : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);
  useEffect(() => { setKeptQs({}); }, [qfStatus, qfAssignee, qfScore, qfGaps, qfMatch, qfConf, qfFit, qfSearch]);
  const [presets, setPresets] = useState([]);
  const [appliedPreset, setAppliedPreset] = useState('');
  const [presetSaveOpen, setPresetSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [bulkStrength, setBulkStrength] = useState('');
  const [bulkFlag, setBulkFlag] = useState('');
  const [comboPresets, setComboPresets] = useState([]);
  const [comboSaveOpen, setComboSaveOpen] = useState(false);
  const [comboName, setComboName] = useState('');
  const [selectedCombo, setSelectedCombo] = useState('');
  const loadComboPresets = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/rfp/response/bulk-presets`, { withCredentials: true });
      setComboPresets(r.data.presets || []);
    } catch { /* viewers have none */ }
  }, []);
  useEffect(() => { loadComboPresets(); }, [loadComboPresets]);
  const applyComboPreset = (p) => {
    setBulkAssignee(p.assignee || '');
    setBulkStrength(p.strength || '');
    setBulkFlag(p.flag || '');
    setSelectedCombo(p.id);
    toast.success(`Combo "${p.name}" loaded — hit Apply to run it on the selected questions`);
  };
  const saveComboPreset = async () => {
    const name = comboName.trim();
    if (!name) { toast.error('Give the combo a name'); return; }
    if (!bulkAssignee && !bulkStrength && !bulkFlag) { toast.error('Pick at least one action first'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/bulk-presets`,
        { name, assignee: bulkAssignee || null, strength: bulkStrength || null, flag: bulkFlag || null },
        { withCredentials: true });
      toast.success(`Combo "${name}" saved for one-click reuse`);
      setComboName(''); setComboSaveOpen(false); setSelectedCombo(r.data.id);
      loadComboPresets();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save combo'); }
  };
  const deleteComboPreset = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/rfp/response/bulk-presets/${id}`, { withCredentials: true });
      setSelectedCombo(''); loadComboPresets(); toast.success('Saved combo deleted');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete combo'); }
  };
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const loadPresets = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/rfp/response/filter-presets`, { withCredentials: true });
      setPresets(r.data.presets || []);
      setPresetsLoaded(true);
    } catch { /* viewers have no presets */ }
  }, []);
  useEffect(() => { loadPresets(); }, [loadPresets]);
  const applyPreset = (p, auto = false) => {
    const f = { qfStatus: 'all', qfAssignee: 'all', qfScore: 'all', qfGaps: false, qfWeakest: false, qfMatch: 'all', qfConf: 'all', qfFit: 'all', qfSearch: '', ...(p.filters || {}) };
    setQfStatus(f.qfStatus); setQfAssignee(f.qfAssignee); setQfScore(f.qfScore);
    setQfGaps(!!f.qfGaps); setQfWeakest(!!f.qfWeakest); setQfMatch(f.qfMatch);
    setQfConf(f.qfConf); setQfFit(f.qfFit); setQfSearch(f.qfSearch || '');
    setAppliedPreset(p.id);
    if (auto) toast.info(`Default filter "${p.name}" applied — pick "Saved filters…" or clear filters to see everything`);
    else toast.success(`Filter "${p.name}" applied`);
  };
  const defaultAppliedRef = useRef(null);
  useEffect(() => {
    if (!presetsLoaded || !project?.id || project.id === defaultAppliedRef.current) return;
    defaultAppliedRef.current = project.id;
    const def = presets.find(p => p.is_default);
    if (def) applyPreset(def, true);
  }, [presetsLoaded, project?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleDefault = async (id) => {
    const p = presets.find(x => x.id === id);
    try {
      await axios.post(`${API_URL}/api/rfp/response/filter-presets/${id}/default`,
        { value: !p?.is_default }, { withCredentials: true });
      toast.success(!p?.is_default
        ? `"${p?.name}" is now your default filter — it applies automatically when you open a project`
        : 'Default filter cleared');
      loadPresets();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update default filter'); }
  };
  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error('Give the filter a name'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/filter-presets`,
        { name, filters: { qfStatus, qfAssignee, qfScore, qfGaps, qfWeakest, qfMatch, qfConf, qfFit, qfSearch } },
        { withCredentials: true });
      toast.success(`Filter "${name}" saved — reapply it any time from Saved filters`);
      setPresetName(''); setPresetSaveOpen(false); setAppliedPreset(r.data.id);
      loadPresets();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save filter'); }
  };
  const deletePreset = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/rfp/response/filter-presets/${id}`, { withCredentials: true });
      setAppliedPreset(''); loadPresets(); toast.success('Saved filter deleted');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete filter'); }
  };
  const [selQs, setSelQs] = useState({});
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [qualityBar, setQualityBar] = useState(() => Number(localStorage.getItem('rfpQualityBar')) || 50);
  const [crunchSending, setCrunchSending] = useState(false);
  const [qCol, setQCol] = useState('auto');
  const [colMap, setColMap] = useState(null);
  const [stageCfg, setStageCfg] = useState({ stage_warn_days: 7, stage_stall_days: 14 });
  const archivedRef = useRef(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeForm, setNudgeForm] = useState({ days: 3, muted: false });
  const [reminderForm, setReminderForm] = useState({ enabled: true, days: 3 });
  const [winStoriesOpen, setWinStoriesOpen] = useState(false);
  const [insertTarget, setInsertTarget] = useState(null);
  const [narrTarget, setNarrTarget] = useState(null);
  const [starSugg, setStarSugg] = useState([]);
  const [starSuggBusy, setStarSuggBusy] = useState(false);
  const [starSuggHidden, setStarSuggHidden] = useState({});
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/response/projects?archived=${archivedRef.current}`, { withCredentials: true });
      setProjects(res.data?.projects || []);
    } catch { setProjects([]); }
  }, []);

  const loadViews = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/response/filter-views`, { withCredentials: true });
      setViews(res.data?.views || []);
    } catch { /* views optional */ }
  }, []);

  const refreshAssignees = useCallback(() => {
    axios.get(`${API_URL}/api/rfp/response/assignees`, { withCredentials: true })
      .then(res => setAssignees(res.data?.assignees || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadProjects();
    loadViews();
    axios.get(`${API_URL}/api/rfp/response/settings`, { withCredentials: true })
      .then(r => setStageCfg(r.data)).catch(() => {});
    refreshAssignees();
    const deepLink = new URLSearchParams(window.location.search).get('project');
    if (deepLink) openProject(deepLink);
    const deepTab = new URLSearchParams(window.location.search).get('tab');
    if (deepLink && deepTab) setTimeout(() => setRfpTab(deepTab), 400);
    const deepDid = new URLSearchParams(window.location.search).get('did');
    if (deepLink && deepDid) setTimeout(() => setNarrFocus({ did: deepDid, sid: new URLSearchParams(window.location.search).get('sid') || null, t: Date.now() }), 600);
    const deepRow = new URLSearchParams(window.location.search).get('row'), deepDoc = new URLSearchParams(window.location.search).get('doc');
    if (deepLink && deepDoc) setTimeout(() => {
      const el = (deepRow !== null && document.querySelector(`[data-testid="rfp-resp-q-${deepDoc}-${deepRow}"]`)) || document.getElementById(`doc-${deepDoc}`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-[#4ABFCF]', 'rounded-md'); setTimeout(() => el.classList.remove('ring-2', 'ring-[#4ABFCF]', 'rounded-md'), 6000); }
    }, 1500);
    if (new URLSearchParams(window.location.search).get('reviewers')) setReviewersOpen(true);
    const binFlag = new URLSearchParams(window.location.search).get('bin');
    if (binFlag === 'restored') setTimeout(() => toast.success('Restored from the bin — it is back exactly where it was'), 800);
    if (binFlag === 'expired') setTimeout(() => toast.error('That restore link has already been used or the item has cleared from the bin'), 800);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadProjects]);

  const openProject = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/response/projects/${id}`, { withCredentials: true });
      setProject(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load the project — try again');
      return;
    }
    clearInterval(pollRef.current);
    let misses = 0;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/rfp/response/projects/${id}`, { withCredentials: true });
        misses = 0;
        setProject(r.data);
        const stillBusy = (r.data.documents || []).some(d => BUSY.includes(d.status))
          || r.data.intake?.status === 'analysing' || (r.data.deliverables || []).some(d => d.drafting);
        if (!stillBusy) clearInterval(pollRef.current);
      } catch {
        misses += 1;
        if (misses >= 10) {
          clearInterval(pollRef.current);
          toast.error('Lost connection while refreshing — reopen the project to resume');
        }
      }
    }, 3500);
  }, []);

  const createProject = async () => {
    if (!newName.trim()) { toast.error('Give the RFP project a name'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects`, { name: newName.trim() }, { withCredentials: true });
      setNewName('');
      setProject(res.data);
      loadProjects();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not create project'); }
  };

  const doUpload = async (file, col) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (col) fd.append('question_column', col);
      await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents`, fd, { withCredentials: true });
      toast.success(`"${file.name}" uploaded — AI is extracting and matching questions${col ? ` from column ${col}` : ''}`);
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); setColMap(null); if (fileRef.current) fileRef.current.value = ''; }
  };

  const upload = async (file) => {
    if (!file || !project) return;
    if (/\.(xls|xlsx)$/i.test(file.name)) {
      setColMap({ file, columns: null });
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/api/rfp/response/preview-columns`, fd, { withCredentials: true });
        setColMap({ file, columns: res.data.columns });
      } catch { setColMap(null); doUpload(file, ''); }
      return;
    }
    doUpload(file, '');
  };

  const removeProject = async (p) => {
    if (!window.confirm(`Delete RFP project "${p.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/rfp/response/projects/${p.id}`, { withCredentials: true });
      loadProjects();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete'); }
  };

  const archiveProject = async (p, archived) => {
    try {
      await axios.put(`${API_URL}/api/rfp/response/projects/${p.id}/meta`, { archived }, { withCredentials: true });
      toast.success(archived ? `"${p.name}" archived` : `"${p.name}" restored`);
      loadProjects();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
  };

  const autoDraftDoc = async (docId) => {
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/auto-draft`, {}, { withCredentials: true });
      toast.success(`Auto-drafting ${r.data.rows} unanswered row${r.data.rows === 1 ? '' : 's'} from narrative + library sources…`);
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not start auto-draft'); }
  };
  const buildDoc = async (docId) => {
    try {
      await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/build`, {}, { withCredentials: true });
      toast.success('Drafting responses from matched capabilities…');
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not start response build'); }
  };

  const updateQuestion = async (docId, idx, fields, localPatch) => {
    if (!fields && localPatch) {
      setProject(prev => ({ ...prev, documents: prev.documents.map(d => d.id === docId ? { ...d, questions: d.questions.map(q => (q.idx === idx ? { ...q, ...localPatch } : q)) } : d) }));
      return;
    }
    try {
      const res = await axios.put(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/questions/${idx}`,
        fields, { withCredentials: true });
      const { _doc_scores: newScores, ...updatedQ } = res.data || {};
      setProject(prev => ({
        ...prev,
        documents: prev.documents.map(d => d.id === docId
          ? { ...d, ...(newScores ? { scores: newScores } : {}), questions: d.questions.map(q => (q.idx === idx ? updatedQ : q)) } : d),
      }));
      if (fields.assigned_to) toast.success(`Assigned to ${fields.assigned_to} — they've been notified`);
      if ('assigned_to' in fields) refreshAssignees();
      const k = `${docId}:${idx}`;
      setKeptQs(prev => (prev[k] ? prev : { ...prev, [k]: true }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update question'); }
  };

  const matrixFillDoc = async (docId) => {
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/matrix-fill`, {}, { withCredentials: true });
      toast.success(r.data.queued ? `Drafting matrix columns for ${r.data.queued} row${r.data.queued === 1 ? '' : 's'} in the background` : 'Every answered row already has its columns');
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not start auto-fill'); }
  };
  const [intakeSummaryOpen, setIntakeSummaryOpen] = useState(false);
  const [structureOpen, setStructureOpen] = useState(false);
  const [previewShareOpen, setPreviewShareOpen] = useState(false);
  const [narrFocus, setNarrFocus] = useState(null);
  /** "assign to…" on a line of the empty list — question rows use the normal assignment, sections get their own assignee. */
  const assignGap = async (it, who) => {
    if (it.kind === 'question') { await updateQuestion(it.docId, it.idx, { assigned_to: who }); return; }
    try {
      await axios.put(`${API_URL}/api/rfp/response/projects/${project.id}/deliverables/${it.did}/sections/${it.sid}/assign`, { assignee: who }, { withCredentials: true });
      setProject((prev) => (prev ? { ...prev, deliverables: prev.deliverables.map((d) => (d.id === it.did ? { ...d, sections: (d.sections || []).map((s) => (s.id === it.sid ? { ...s, assigned_to: who } : s)) } : d)) } : prev));
      toast.success(who ? `Section handed to ${who.split('@')[0]}` : 'Section unassigned');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not assign'); }
  };
  /** From the "what's still empty" list: open the section's editor or scroll to the blank answer row. */
  const jumpToGap = (it) => {
    if (it.kind === 'section') { setNarrFocus({ did: it.did, sid: it.sid, t: Date.now() }); setRfpTab('narratives'); return; }
    setRfpTab('annexures');
    collapse.reveal(it.docId, ((project?.documents || []).find(d => d.id === it.docId)?.questions || []).find(q => q.idx === it.idx)?.section);
    const find = () => document.querySelector(`[data-testid="rfp-resp-q-${it.docId}-${it.idx}"]`);
    setTimeout(() => {
      let el = find();
      if (!el) { setQfStatus('all'); setQfAssignee('all'); setQfScore('all'); setQfGaps(false); setQfWeakest(false); setQfMatch('all'); setQfConf('all'); setQfFit('all'); setQfSearch(''); }
      setTimeout(() => {
        el = find();
        if (!el) { toast.info('That row is in a sheet that is still matching — open it from the Annexures tab'); return; }
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-amber-400', 'rounded-md');
        setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'rounded-md'), 2500);
      }, el ? 0 : 300);
    }, 200);
  };
  const [rfpTab, setRfpTab] = useState('container');
  const [buildRecap, setBuildRecap] = useState(null);
  const [reviewersOpen, setReviewersOpen] = useState(false);
  const [scaleDoc, setScaleDoc] = useState(null);
  const [remapDoc, setRemapDoc] = useState(null);
  useEffect(() => {
    const drive = new URLSearchParams(window.location.search).get('drive');
    if (!drive) return;
    if (drive === 'connected') toast.success('Google Drive connected — open the Attachment library to browse your folder');
    else if (drive === 'scope') toast.error('Google Drive: read-only Drive access was not granted');
    else toast.error('Google Drive connection failed — try again');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const saveName = () => {
    const nm = nameDraft.trim();
    setEditingName(false);
    if (nm && nm !== project.name) updateMeta({ name: nm });
  };
  const updateMeta = async (fields) => {
    try {
      const res = await axios.put(`${API_URL}/api/rfp/response/projects/${project.id}/meta`, fields, { withCredentials: true });
      setProject(prev => ({ ...prev, ...res.data }));
      toast.success(fields.outcome ? `Outcome recorded: ${fields.outcome}`
        : fields.name ? 'Project renamed'
        : fields.loss_reasons ? 'Loss reasons updated'
        : fields.owner ? `Owner changed to ${fields.owner} — they've been notified`
        : fields.due_date === '' ? 'Due date cleared'
        : fields.due_date ? `Due date set — ${fields.due_date}` : 'Updated');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update'); }
  };

  useEffect(() => {
    if (project?.outcome === 'won') {
      axios.get(`${API_URL}/api/rfp/response/projects/${project.id}/star-suggestions`, { withCredentials: true })
        .then(res => setStarSugg(res.data?.suggestions || []))
        .catch(() => setStarSugg([]));
    } else setStarSugg([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.outcome]);

  const acceptStarSuggestions = async () => {
    setStarSuggBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/star-suggestions/accept`,
        { items: starSugg.map(s => ({ doc_id: s.doc_id, idx: s.idx })) }, { withCredentials: true });
      toast.success(`${res.data?.starred || 0} answer${(res.data?.starred || 0) === 1 ? '' : 's'} starred as win stories`);
      setStarSugg([]);
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not star answers'); }
    finally { setStarSuggBusy(false); }
  };

  const moveStage = async (id, stage) => {
    try {
      await axios.put(`${API_URL}/api/rfp/response/projects/${id}/meta`, { stage }, { withCredentials: true });
      setProjects(prev => (prev || []).map(p => (p.id === id ? { ...p, stage } : p)));
      toast.success(`Moved to ${stage.charAt(0).toUpperCase() + stage.slice(1)}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not move'); }
  };

  const applyView = (v) => {
    const f = v.filters || {};
    setSearch(f.search || '');
    setFOutcome(f.outcome || 'all');
    setFOwner(f.owner || 'all');
    setFFrom(f.from || '');
    setFTo(f.to || '');
    setSortKey(f.sortKey || 'due');
    setSortDir(f.sortDir || 'asc');
    toast.success(`View "${v.name}" applied`);
  };

  const saveView = async () => {
    const name = window.prompt('Name this view (e.g. "My live deals")');
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API_URL}/api/rfp/response/filter-views`,
        { name: name.trim(), filters: { search, outcome: fOutcome, owner: fOwner, from: fFrom, to: fTo, sortKey, sortDir } },
        { withCredentials: true });
      toast.success(`View "${name.trim()}" saved`);
      loadViews();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save the view'); }
  };

  const deleteView = async (v) => {
    try {
      await axios.delete(`${API_URL}/api/rfp/response/filter-views/${v.id}`, { withCredentials: true });
      toast.success(`View "${v.name}" deleted`);
      loadViews();
    } catch { toast.error('Could not delete the view'); }
  };

  const rematchAll = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/rematch-all`, {}, { withCredentials: true });
      toast.success(`Re-matching ${res.data.rematching} document${res.data.rematching === 1 ? '' : 's'} against the current library`);
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not start re-match'); }
  };

  const rematchDoc = async (docId) => {
    try {
      await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/rematch`, {}, { withCredentials: true });
      toast.success('Re-matching against the current library — answers, flags and assignments are kept');
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not start re-match'); }
  };

  const deleteAnnexure = async (d) => {
    const n = d.question_count || (d.questions || []).length;
    const answered = (d.questions || []).filter(q => q.response).length;
    if (!window.confirm(`Delete “${d.filename}”${n ? ` and its ${n} question${n === 1 ? '' : 's'}` : ''}${answered ? ` (${answered} answered)` : ''}?\n\nIt goes to the bin at the bottom of this list — you can restore it from there if this was a mistake.`)) return;
    try {
      await axios.delete(`${API_URL}/api/rfp/response/projects/${project.id}/annexures/${d.id}`, { withCredentials: true });
      toast.success(`“${d.filename}” moved to the bin`, {
        action: { label: 'Undo', onClick: async () => { try { await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/annexures/${d.id}/restore`, {}, { withCredentials: true }); toast.success(`“${d.filename}” restored`); openProject(project.id); } catch (err) { toast.error(err?.response?.data?.detail || 'Could not restore'); } } },
      });
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete'); }
  };

  const reparseDoc = async (docId) => {
    if (!window.confirm('Re-parse this document with the latest extraction logic? Answers for substantially-unchanged questions carry over.')) return;
    try {
      await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/reparse`, {}, { withCredentials: true });
      toast.success('Re-parsing the document — questions will refresh shortly');
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not re-parse'); }
  };

  const crunchPreview = async () => {
    setCrunchSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/crunch-preview`, {}, { withCredentials: true });
      toast.success(`Crunch preview sent to ${res.data.sent_to}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail.length < 300 ? detail : 'Could not send the preview');
    }
    finally { setCrunchSending(false); }
  };

  const replaceDoc = async (docId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/replace`, fd, { withCredentials: true });
      toast.success(`Updated document uploaded — re-extracting; matching answers carry over automatically`);
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not replace the document'); }
  };

  const scoreResponse = async () => {
    setScoring(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/score-response`, {}, { withCredentials: true });
      setProject(prev => ({ ...prev, response_score: res.data,
        score_history: [...(prev.score_history || []), { overall: res.data.overall, scored_at: res.data.scored_at, scored_by: res.data.scored_by }].slice(-10) }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not score the response'); }
    finally { setScoring(false); }
  };

  const [auditBusy, setAuditBusy] = useState(false);
  const downloadAudit = async () => {
    setAuditBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/rfp/response/projects/${project.id}/audit.pdf`, { withCredentials: true, responseType: 'blob' });
      saveAs(res.data, `${project.name.replace(/[^A-Za-z0-9 _-]/g, '')} - audit trail.pdf`);
      toast.success('Audit trail PDF downloaded');
    } catch (err) { toast.error('Could not build the audit trail PDF'); }
    finally { setAuditBusy(false); }
  };
  const exportFile = async (kind) => {
    const allQs = (project.documents || []).flatMap(d => d.questions || []);
    const answered = allQs.filter(q => q.response);
    const unanswered = allQs.length - answered.length;
    const weak = answered.filter(q => qualityOf(q) < qualityBar);
    if (weak.length > 0 || unanswered > 0) {
      const parts = [];
      if (weak.length > 0) parts.push(`${weak.length} answer${weak.length === 1 ? '' : 's'} score${weak.length === 1 ? 's' : ''} below your quality bar of Q${qualityBar}`);
      if (unanswered > 0) parts.push(`${unanswered} question${unanswered === 1 ? ' has' : 's have'} no answer yet`);
      if (!window.confirm(`${parts.join(' and ')}. Export anyway?`)) return;
    }
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/rfp/response/projects/${project.id}/export/${kind}`,
        { withCredentials: true, responseType: 'blob' });
      saveAs(res.data, `${project.name.replace(/[^A-Za-z0-9 _-]/g, '')}-Response.${kind}`);
    } catch (err) { toast.error('Could not export — is at least one document processed?'); }
    finally { setExporting(false); }
  };

  const [wordingReview, setWordingReview] = useState(null); // promotion gate: answer contains client names / pricing / people

  const addToLibrary = async (docId, idx, wording = {}) => {
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/questions/${idx}/to-library`,
        wording, { withCredentials: true });
      if (res.data.needs_review) { setWordingReview({ ...res.data, kind: 'add', docId, idx }); return; }
      setWordingReview(null);
      toast.success(`Added to the library as ${res.data.capability_id} — "${res.data.title}"`);
      setProject(prev => ({
        ...prev,
        documents: prev.documents.map(d => d.id === docId
          ? { ...d, questions: d.questions.map(q => (q.idx === idx ? { ...q, library_capability_id: res.data.capability_id } : q)) } : d),
      }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not add to library'); }
  };

  const promoteToLibrary = async (docId, idx, wording = {}) => {
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${docId}/questions/${idx}/promote-to-library`,
        wording, { withCredentials: true });
      if (res.data.needs_review) { setWordingReview({ ...res.data, kind: 'promote', docId, idx }); return; }
      setWordingReview(null);
      toast.success(`Library updated — ${res.data.capability_id} "${res.data.title}" now carries this wording. The previous version is kept in the block's history and can be reverted from the Library.`);
      setProject(prev => ({
        ...prev,
        documents: prev.documents.map(d => d.id === docId
          ? { ...d, questions: d.questions.map(q => (q.idx === idx
            ? { ...q, promoted_to_library_at: res.data.promoted_to_library_at, promoted_to_library_by: res.data.promoted_to_library_by,
                library_capability_id: res.data.capability_id, library_block_id: res.data.block_id } : q)) } : d),
      }));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not promote to the library'); }
  };

  const shareLink = (token) => `${window.location.origin}/rfp-share/${token}`;

  const shareProject = async (expiresDays) => {
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/share`,
        { expires_days: expiresDays ?? null }, { withCredentials: true });
      setProject(prev => ({ ...prev, share_token: res.data.token, share_expires_at: res.data.expires_at }));
      try { await navigator.clipboard.writeText(shareLink(res.data.token)); } catch { /* clipboard optional */ }
      toast.success(`Read-only share link copied${expiresDays ? ` — expires in ${expiresDays} days` : ''}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not create the share link'); }
  };

  const saveNudgeSettings = async () => {
    try {
      const res = await axios.put(`${API_URL}/api/rfp/response/projects/${project.id}/nudge-settings`, nudgeForm, { withCredentials: true });
      setProject(prev => ({ ...prev, nudge_settings: res.data }));
      setNudgeOpen(false);
      toast.success(res.data.muted ? 'Step nudges muted for this RFP' : `Owner nudged after ${res.data.days} days on one step`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save nudge settings'); }
  };
  const saveReminderSettings = async () => {
    try {
      const res = await axios.put(`${API_URL}/api/rfp/response/projects/${project.id}/reminder-settings`,
        reminderForm, { withCredentials: true });
      setProject(prev => ({ ...prev, reminder_settings: res.data }));
      setReminderOpen(false);
      toast.success(res.data.enabled ? `Reminders after ${res.data.days} day${res.data.days === 1 ? '' : 's'}` : 'Reminders switched off for this project');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save reminder settings'); }
  };

  const revokeShare = async () => {
    try {
      await axios.delete(`${API_URL}/api/rfp/response/projects/${project.id}/share`, { withCredentials: true });
      setProject(prev => ({ ...prev, share_token: null }));
      toast.success('Share link revoked');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not revoke'); }
  };

  if (user?.role === 'user') {
    return <p className="text-sm text-slate-500 p-10 text-center" data-testid="rfp-resp-blocked">The RFP Response Builder is not available for view-only accounts.</p>;
  }

  const matchesFilters = (q) => {
    const hasScale = !!project?.scoring_scheme?.levels?.length;
    const s = qfSearch.trim().toLowerCase();
    return (qfStatus === 'all' || q.flag === qfStatus) &&
      (qfAssignee === 'all' || (qfAssignee === 'unassigned' ? !q.assigned_to : q.assigned_to === qfAssignee)) &&
      (!qfGaps || q.strength === 'none' || !!q.gap_note || q.flag === 'manual_required') &&
      (!hasScale || qfScore === 'all' ||
        (qfScore === 'unscored' ? (!!q.response && !q.client_score) : q.client_score === qfScore)) &&
      (qfMatch === 'all' || (qfMatch === 'pricing' ? !!q.is_pricing : q.strength === qfMatch && !q.is_pricing)) &&
      (qfConf === 'all' || q.confidence === qfConf) &&
      (qfFit === 'all' || q.fit === qfFit) &&
      (!s || `${q.text} ${q.response || ''}`.toLowerCase().includes(s));
  };

  const filterQuestions = (qs, docId) => {
    const out = qs.filter(q => matchesFilters(q) || (docId && keptQs[`${docId}:${q.idx}`]));
    return qfWeakest ? [...out].sort((a, b) => qualityOf(a) - qualityOf(b)) : out;
  };

  const toggleSelQ = (docId, idx) => setSelQs(prev => {
    const k = `${docId}:${idx}`;
    const next = { ...prev };
    if (next[k]) delete next[k]; else next[k] = true;
    return next;
  });

  const bulkApplyCombo = async () => {
    if (!bulkAssignee && !bulkStrength && !bulkFlag) { toast.error('Pick an assignee, strength or status to apply'); return; }
    const byDoc = {};
    Object.keys(selQs).forEach(k => {
      const [d, i] = k.split(':');
      (byDoc[d] = byDoc[d] || []).push(Number(i));
    });
    try {
      const totals = { assigned: 0, strength: 0, flag: 0 };
      for (const [d, idxs] of Object.entries(byDoc)) {
        const res = await axios.post(`${API_URL}/api/rfp/response/projects/${project.id}/documents/${d}/bulk-update`,
          { idxs, assignee: bulkAssignee || null, strength: bulkStrength || null, flag: bulkFlag || null },
          { withCredentials: true });
        totals.assigned += res.data.assigned; totals.strength += res.data.strength; totals.flag += res.data.flag;
      }
      const parts = [];
      if (totals.assigned) parts.push(`${totals.assigned} assigned to ${bulkAssignee}`);
      if (totals.strength) parts.push(`${totals.strength} set to ${bulkStrength === 'none' ? 'Gap' : bulkStrength}`);
      if (totals.flag) parts.push(`${totals.flag} marked "${FLAG_LABELS[bulkFlag] || bulkFlag}"`);
      toast.success(parts.length ? `Bulk update applied — ${parts.join(' · ')}` : 'No changes needed — questions already matched');
      setKeptQs(prev => ({ ...prev, ...Object.fromEntries(Object.keys(selQs).map(k => [k, true])) }));
      setSelQs({}); setBulkAssignee(''); setBulkStrength(''); setBulkFlag('');
      if (totals.assigned) refreshAssignees();
      openProject(project.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Bulk update failed'); }
  };

  const anyProcessed = (project?.documents || []).some(d => ['ready', 'built'].includes(d.status));
  const canManage = ['admin', 'super_admin'].includes(user?.role);

  const owners = [...new Set((projects || []).map(p => p.created_by).filter(Boolean))].sort();
  const openByOwner = (projects || []).reduce((acc, p) => {
    if (!p.archived) acc[p.created_by] = (acc[p.created_by] || 0) + (p.open_questions || 0);
    return acc;
  }, {});
  const sq = search.trim().toLowerCase();
  const filtered = (projects || []).filter(p =>
    (!sq || `${p.name} ${p.client_name || ''} ${p.created_by || ''}`.toLowerCase().includes(sq)) &&
    (fOutcome === 'all' || (p.outcome || 'pending') === fOutcome) &&
    (fOwner === 'all' || p.created_by === fOwner) &&
    (!fFrom || (p.due_date && p.due_date >= fFrom)) &&
    (!fTo || (p.due_date && p.due_date <= fTo)));
  const dir = sortDir === 'asc' ? 1 : -1;
  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === 'due') {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date < b.due_date ? -dir : a.due_date > b.due_date ? dir : 0;
    }
    const va = (sortKey === 'owner' ? a.created_by : (a.outcome || 'pending')) || '';
    const vb = (sortKey === 'owner' ? b.created_by : (b.outcome || 'pending')) || '';
    return va < vb ? -dir : va > vb ? dir : 0;
  });

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 max-w-6xl mx-auto" data-testid="rfp-response-page">
      <h1 className="text-2xl font-bold text-[#0F2040] flex items-center gap-2 mb-1">
        <FileQuestion className="w-6 h-6 text-[#4ABFCF]" />RFP Response Builder
        <span className="flex-1" />
        <AiJobsChip />
      </h1>
      <p className="text-sm text-slate-500 mb-5">
        Load a client RFP, match every question against the Capability Library, draft source-grounded answers, assign and review them, then score and export the finished response.
      </p>

      {!project ? (
        <div className="space-y-4">
          <div className="flex items-center gap-1 border-b border-slate-100 pb-2">
            <Button size="sm" variant={view === 'projects' ? 'default' : 'ghost'}
              className={`h-7 text-xs ${view === 'projects' ? 'bg-[#0F2040] hover:bg-[#1A3055] text-white' : ''}`}
              onClick={() => setView('projects')} data-testid="rfp-resp-tab-projects">
              <FolderKanban className="w-3.5 h-3.5 mr-1" />Projects
            </Button>
            <Button size="sm" variant={view === 'pipeline' ? 'default' : 'ghost'}
              className={`h-7 text-xs ${view === 'pipeline' ? 'bg-[#0F2040] hover:bg-[#1A3055] text-white' : ''}`}
              onClick={() => setView('pipeline')} data-testid="rfp-resp-tab-pipeline">
              <Kanban className="w-3.5 h-3.5 mr-1" />Pipeline
            </Button>
            <Button size="sm" variant={view === 'mine' ? 'default' : 'ghost'}
              className={`h-7 text-xs ${view === 'mine' ? 'bg-[#0F2040] hover:bg-[#1A3055] text-white' : ''}`}
              onClick={() => setView('mine')} data-testid="rfp-resp-tab-mine">
              <ListChecks className="w-3.5 h-3.5 mr-1" />My Questions
            </Button>
            <Button size="sm" variant={view === 'workload' ? 'default' : 'ghost'}
              className={`h-7 text-xs ${view === 'workload' ? 'bg-[#0F2040] hover:bg-[#1A3055] text-white' : ''}`}
              onClick={() => setView('workload')} data-testid="rfp-resp-tab-workload">
              <Users className="w-3.5 h-3.5 mr-1" />Workload
            </Button>
            <Button size="sm" variant={view === 'deadlines' ? 'default' : 'ghost'}
              className={`h-7 text-xs ${view === 'deadlines' ? 'bg-[#0F2040] hover:bg-[#1A3055] text-white' : ''}`}
              onClick={() => setView('deadlines')} data-testid="rfp-resp-tab-deadlines">
              <CalendarDays className="w-3.5 h-3.5 mr-1" />Deadlines
            </Button>
          </div>
          {view === 'mine' ? (
            <MyQuestionsView assignees={assignees} canManage={canManage} onOpenProject={openProject} />
          ) : view === 'workload' ? (
            <WorkloadView />
          ) : view === 'pipeline' ? (
            <div className="space-y-2">
              {canManage && (
                <div className="space-y-1">
                <div className="flex items-center justify-end gap-1.5 text-[11px] text-slate-500" data-testid="rfp-stage-threshold-editor">
                  Flag after
                  <input type="number" min="1" max="90" value={stageCfg.stage_warn_days}
                    onChange={e => setStageCfg(c => ({ ...c, stage_warn_days: Number(e.target.value) }))}
                    className="h-6 w-12 text-[11px] border border-slate-200 rounded px-1 text-slate-600" data-testid="rfp-stage-warn-input" />
                  d · stalled after
                  <input type="number" min="2" max="180" value={stageCfg.stage_stall_days}
                    onChange={e => setStageCfg(c => ({ ...c, stage_stall_days: Number(e.target.value) }))}
                    className="h-6 w-12 text-[11px] border border-slate-200 rounded px-1 text-slate-600" data-testid="rfp-stage-stall-input" />
                  d · offer saved layouts from
                  <input type="number" min="0" max="100" value={stageCfg.template_fit_threshold ?? 50}
                    onChange={e => setStageCfg(c => ({ ...c, template_fit_threshold: Number(e.target.value) }))}
                    className="h-6 w-12 text-[11px] border border-slate-200 rounded px-1 text-slate-600" title="A client's saved layout is only raised in the bell when it fits the freshly parsed RFP at least this well" data-testid="rfp-template-fit-threshold-input" />
                  % fit
                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" data-testid="rfp-stage-threshold-save"
                    onClick={async () => {
                      try {
                        const r = await axios.put(`${API_URL}/api/rfp/response/settings`, stageCfg, { withCredentials: true });
                        setStageCfg(r.data);
                        toast.success(`Saved — flag ${r.data.stage_warn_days}d, stalled ${r.data.stage_stall_days}d, layouts offered from ${r.data.template_fit_threshold}% fit`);
                      } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save thresholds'); }
                    }}>Save</Button>
                </div>
                <ClientFitBars settings={stageCfg} onChanged={setStageCfg} />
                </div>
              )}
              <PipelineBoard projects={(projects || []).filter(p => !p.archived)} onOpenProject={openProject} onMove={moveStage}
                thresholds={{ warn: stageCfg.stage_warn_days, stall: stageCfg.stage_stall_days }} />
            </div>
          ) : view === 'deadlines' ? (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" className="h-7 text-[11px] text-slate-600" onClick={crunchPreview}
                  disabled={crunchSending} title="Sends you the Monday crunch summary email right now so you can see the format"
                  data-testid="rfp-crunch-preview-btn">
                  {crunchSending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                  Email me a crunch preview
                </Button>
              </div>
              <DeadlineCalendar projects={(projects || []).filter(p => !p.archived)} onOpenProject={openProject} />
            </div>
          ) : (
            <>
          <RfpInsights projects={showArchived ? [] : (projects || [])} />
          <div className="flex gap-2 max-w-md">
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New RFP project name (e.g. ACME Corp 2026 RFP)"
              onKeyDown={e => e.key === 'Enter' && createProject()} className="h-9 text-sm" data-testid="rfp-resp-name-input" />
            <Button size="sm" onClick={createProject} className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-9" data-testid="rfp-resp-create-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Create
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="rfp-resp-filter-bar">
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, client or owner…"
              className="h-8 w-56 text-xs" data-testid="rfp-resp-search-input" />
            <select value={fOutcome} onChange={e => setFOutcome(e.target.value)}
              className="h-8 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
              data-testid="rfp-resp-filter-outcome">
              <option value="all">All outcomes</option>
              <option value="pending">Pending</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
            <select value={fOwner} onChange={e => setFOwner(e.target.value)}
              className="h-8 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600 max-w-[180px]"
              data-testid="rfp-resp-filter-owner">
              <option value="all">All owners</option>
              {owners.map(o => <option key={o} value={o}>{`${o} · ${openByOwner[o] || 0} open`}</option>)}
            </select>
            <input type="date" value={fFrom} onChange={e => setFFrom(e.target.value)} title="Due date from"
              className="h-8 text-[11px] border border-slate-200 rounded-md px-1.5 text-slate-600" data-testid="rfp-resp-filter-from" />
            <span className="text-[10px] text-slate-400">to</span>
            <input type="date" value={fTo} onChange={e => setFTo(e.target.value)} title="Due date to"
              className="h-8 text-[11px] border border-slate-200 rounded-md px-1.5 text-slate-600" data-testid="rfp-resp-filter-to" />
            <select value={sortKey} onChange={e => setSortKey(e.target.value)}
              className="h-8 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
              data-testid="rfp-resp-sort-key">
              <option value="due">Sort: due date</option>
              <option value="owner">Sort: owner</option>
              <option value="outcome">Sort: outcome</option>
            </select>
            <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-500"
              onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
              title={sortDir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
              data-testid="rfp-resp-sort-dir">
              {sortDir === 'asc' ? <ArrowUpNarrowWide className="w-3.5 h-3.5" /> : <ArrowDownWideNarrow className="w-3.5 h-3.5" />}
            </Button>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
              <Switch checked={showArchived}
                onCheckedChange={v => { archivedRef.current = v; setShowArchived(v); setProjects(null); loadProjects(); }}
                data-testid="rfp-resp-archived-toggle" />
              Archived
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 text-xs text-slate-600" data-testid="rfp-resp-views-btn">
                  <Bookmark className="w-3.5 h-3.5 mr-1" />Views{views.length > 0 ? ` (${views.length})` : ''}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {views.map(v => (
                  <DropdownMenuItem key={v.id} onClick={() => applyView(v)} className="flex items-center justify-between gap-3"
                    data-testid={`rfp-resp-view-item-${v.name}`}>
                    <span className="truncate max-w-[160px]">{v.name}</span>
                    <button onClick={e => { e.stopPropagation(); deleteView(v); }} className="text-slate-300 hover:text-red-600 shrink-0"
                      title="Delete this view" data-testid={`rfp-resp-view-delete-${v.name}`}>
                      <XCircle className="w-3 h-3" />
                    </button>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={saveView} data-testid="rfp-resp-view-save">
                  <Plus className="w-3 h-3 mr-1" />Save current view…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" className="h-8 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
              onClick={() => setWinStoriesOpen(true)} title="Browse starred win-story answers by topic"
              data-testid="rfp-resp-win-stories-btn">
              <Star className="w-3.5 h-3.5 mr-1 fill-amber-400 text-amber-500" />Win stories
            </Button>
            {canManage && (
              <Button size="sm" variant="outline" className="h-8 text-xs text-slate-600"
                onClick={() => setFileAdminOpen(true)} title="Manage uploaded files — usage and cleanup"
                data-testid="rfp-resp-files-btn">
                <HardDrive className="w-3.5 h-3.5 mr-1" />Files
              </Button>
            )}
          </div>
          {canManage && owners.length > 0 && !showArchived && (
            <div className="flex flex-wrap gap-1.5 items-center" data-testid="rfp-owner-workload">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Owner workload:</span>
              {owners.map(o => (
                <button key={o} onClick={() => setFOwner(fOwner === o ? 'all' : o)}
                  className={`text-[10px] rounded-full border px-2 py-0.5 transition-colors ${fOwner === o ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#4ABFCF]'}`}
                  title={`${o} — ${openByOwner[o] || 0} open questions across their RFPs. Click to filter.`}
                  data-testid={`rfp-owner-chip-${o}`}>
                  {o.split('@')[0]} · {openByOwner[o] || 0} open
                </button>
              ))}
            </div>
          )}
          {projects === null ? <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" /> : projects.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="rfp-resp-empty">{showArchived ? 'No archived projects.' : 'No RFP projects yet — create one above.'}</p>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="rfp-resp-no-match">No projects match the current filters.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sorted.map(p => (
                <Card key={p.id} className="border-slate-200 hover:border-[#4ABFCF] cursor-pointer transition-colors" data-testid={`rfp-resp-project-${p.id}`}>
                  <CardContent className="p-4 flex items-center gap-3" onClick={() => { setRfpTab('container'); openProject(p.id); }}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0F2040] truncate flex items-center gap-1.5">
                        {p.name}
                        {(() => { const b = dueBadge(p.due_date); return b && !OUTCOME_CLS[p.outcome]
                          ? <Badge variant="outline" className={`text-[9px] shrink-0 ${b.cls}`} data-testid={`rfp-resp-card-due-${p.id}`}>{b.text}</Badge> : null; })()}
                        {OUTCOME_CLS[p.outcome] && (
                          <Badge variant="outline" className={`text-[9px] shrink-0 uppercase font-bold ${OUTCOME_CLS[p.outcome]}`} data-testid={`rfp-resp-card-outcome-${p.id}`}>
                            {p.outcome}{p.response_score ? ` · ${p.response_score.overall.confidence_score}%` : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400">{p.documents.length} document{p.documents.length === 1 ? '' : 's'} · {p.created_by} · {p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}{p.open_questions > 0 ? ` · ${p.open_questions} open` : ''}</p>
                      {(p.signoffs || []).length > 0 && (
                        <div className="flex gap-1.5 mt-1" data-testid={`rfp-resp-signoff-tally-${p.id}`}>
                          {p.signoffs.filter(s => s.decision === 'approved').length > 0 && (
                            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                              {p.signoffs.filter(s => s.decision === 'approved').length} approved
                            </Badge>
                          )}
                          {p.signoffs.filter(s => s.decision !== 'approved').length > 0 && (
                            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">
                              {p.signoffs.filter(s => s.decision !== 'approved').length} changes requested
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); archiveProject(p, !p.archived); }}
                      className="text-slate-300 hover:text-[#0F2040]" title={p.archived ? 'Restore this RFP' : 'Archive this RFP'}
                      data-testid={`rfp-resp-archive-${p.id}`}>
                      {p.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); removeProject(p); }} className="text-slate-300 hover:text-red-600"
                      data-testid={`rfp-resp-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => { setProject(null); clearInterval(pollRef.current); loadProjects(); }}
              data-testid="rfp-resp-back-btn"><ArrowLeft className="w-3 h-3 mr-1" />All projects</Button>
            {editingName ? (
              <input autoFocus value={nameDraft} onChange={e => setNameDraft(e.target.value)} maxLength={120}
                onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                onBlur={saveName}
                className="h-8 text-lg font-bold text-[#0F2040] border border-slate-300 rounded-md px-2 min-w-[280px]"
                data-testid="rfp-resp-name-input" />
            ) : (
              <h2 className="text-lg font-bold text-[#0F2040] flex items-center gap-1.5 group">{project.name}
                <button onClick={() => { setNameDraft(project.name); setEditingName(true); }}
                  title="Rename this RFP project" className="text-slate-300 hover:text-[#0F2040] transition-colors"
                  data-testid="rfp-resp-rename-btn"><Pencil className="w-3.5 h-3.5" /></button>
              </h2>
            )}
            {(() => { const b = dueBadge(project.due_date); return b && project.outcome !== 'won' && project.outcome !== 'lost'
              ? <Badge variant="outline" className={`text-[10px] ${b.cls}`} data-testid="rfp-resp-due-badge">{b.text}</Badge> : null; })()}
            {OUTCOME_CLS[project.outcome] && (
              <Badge variant="outline" className={`text-[10px] uppercase font-bold ${OUTCOME_CLS[project.outcome]}`} data-testid="rfp-resp-outcome-badge">{project.outcome}</Badge>
            )}
            <ProjectProfile project={project} onChanged={(r) => setProject((prev) => (prev ? { ...prev, ...r } : prev))} />
            <button type="button" onClick={() => { setNudgeForm({ days: project.nudge_settings?.days || 3, muted: !!project.nudge_settings?.muted }); setNudgeOpen(true); }}
              title={project.nudge_settings?.muted ? 'Step nudges are muted for this RFP' : `Nudge the owner after ${project.nudge_settings?.days || 3} days on one step`}
              className={`h-6 inline-flex items-center gap-1 text-[11px] border rounded-md px-2 ${project.nudge_settings?.muted ? 'border-slate-200 text-slate-400 bg-slate-50' : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'}`} data-testid="rfp-resp-nudge-settings-btn">
              {project.nudge_settings?.muted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5 text-[#4ABFCF]" />}{project.nudge_settings?.muted ? 'Nudges off' : `Nudge after ${project.nudge_settings?.days || 3}d`}
            </button>
            <span className="flex-1" />
            {anyProcessed && (
              <>
                {project.share_token ? (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-cyan-700 border-cyan-200"
                      title={project.share_expires_at ? `Expires ${new Date(project.share_expires_at).toLocaleDateString()}` : 'No expiry'}
                      onClick={async () => { try { await navigator.clipboard.writeText(shareLink(project.share_token)); toast.success('Share link copied'); } catch { toast.info(shareLink(project.share_token)); } }}
                      data-testid="rfp-resp-copy-share-btn">
                      <Share2 className="w-3.5 h-3.5 mr-1" />Copy share link
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-red-600 px-1.5"
                      onClick={revokeShare} title="Revoke share link" data-testid="rfp-resp-revoke-share-btn">
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="rfp-resp-share-btn">
                        <Share2 className="w-3.5 h-3.5 mr-1" />Share
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => shareProject(null)} data-testid="rfp-resp-share-noexpiry">No expiry</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => shareProject(7)} data-testid="rfp-resp-share-7d">Expires in 7 days</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => shareProject(14)} data-testid="rfp-resp-share-14d">Expires in 14 days</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => shareProject(30)} data-testid="rfp-resp-share-30d">Expires in 30 days</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={scoreResponse} disabled={scoring}
                  data-testid="rfp-resp-score-btn">
                  {scoring ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Gauge className="w-3.5 h-3.5 mr-1" />}Score Response
                </Button>
              </>
            )}
          </div>

          {buildRecap && <BuildRecap recap={buildRecap} project={project} onNext={(tab) => { setBuildRecap(null); setRfpTab(tab); }} onClose={() => setBuildRecap(null)} />}
          <StepRail project={project} activeTab={rfpTab} onGo={(s) => { setRfpTab(s.tab); if (s.id === 'load' || s.id === 'sort') setTimeout(() => document.querySelector('[data-testid="rfp-intake-review"], [data-testid="rfp-intake-label"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150); }} />
          <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="rfp-subnav">
            {[
              ['container', 'Container', FolderKanban, null],
              ['checklist', 'Checklist', ListChecks, (project.deliverables || []).length ? `${(project.deliverables || []).filter(d => d.status === 'done').length}/${(project.deliverables || []).length}` : null],
              ['commercials', 'Commercials', DollarSign, project.commercials ? (project.commercials.currency || '') : null],
              ['narratives', 'Narratives', FileText, (() => { const n = (project.deliverables || []).filter(d => d.type === 'narrative' || d.type === 'generated').length; return n ? String(n) : null; })()],
              ['annexures', 'Annexures', FileSpreadsheet, (project.documents || []).length ? String((project.documents || []).length) : null],
              ['attachments', 'Attachments', FileUp, (() => { const a = (project.deliverables || []).filter(d => d.type === 'attachment' || d.type === 'form'); return a.length ? `${a.filter(d => d.attachment).length}/${a.length}` : null; })()],
              ['preview', 'Preview', Eye, null],
              ['history', 'History', History, null],
            ].map(([id, label, Icon, count]) => (
              <button key={id} type="button" onClick={() => setRfpTab(id)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${rfpTab === id ? 'border-[#4ABFCF] text-[#0F2040]' : 'border-transparent text-slate-500 hover:text-[#0F2040]'}`}
                data-testid={`rfp-tab-${id}`}>
                <Icon className="w-3.5 h-3.5" />{label}
                {count && <span className={`text-[9px] px-1.5 rounded-full ${rfpTab === id ? 'bg-[#4ABFCF]/20 text-[#0F2040]' : 'bg-slate-100 text-slate-500'}`}>{count}</span>}
              </button>
            ))}
          </div>

          {rfpTab === 'container' && (<>
          <SubmissionDueBanner project={project} />
          {(project.packages || []).length + (project.submissions || []).length > 0 && <SubmissionLogCard project={project} onRefresh={() => openProject(project.id)} />}
          <div className="border border-slate-200 bg-white rounded-md p-4 space-y-3" data-testid="rfp-container-props">
            <div className="flex items-center gap-2 flex-wrap">
              <FolderKanban className="w-4 h-4 text-[#4ABFCF]" />
              <span className="text-sm font-semibold text-[#0F2040]">RFP properties</span>
              <span className="text-[10px] text-slate-500">Due date · outcome · owner · deal value · currency · reminders</span>
              <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={downloadAudit} disabled={auditBusy}
                title="One-page PDF: submissions, client confirmations, narrative sign-offs, reviewer handovers, package versions & waivers, with an integrity code"
                data-testid="rfp-audit-pdf-btn">
                {auditBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}Audit trail PDF
              </Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={project.due_date || ''} onChange={e => updateMeta({ due_date: e.target.value || '' })}
              title="RFP submission due date" className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 text-slate-600"
              data-testid="rfp-resp-due-input" />
            <select value={project.outcome || 'pending'}
              onChange={e => {
                const v = e.target.value;
                if (v === 'lost') {
                  const note = window.prompt('Why was this RFP lost? (short note — feeds future response improvements)', project.loss_note || '');
                  updateMeta({ outcome: 'lost', loss_note: note || '' });
                } else updateMeta({ outcome: v });
              }}
              title="Deal outcome" className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
              data-testid="rfp-resp-outcome-select">
              <option value="pending">Outcome: pending</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
            {canManage && assignees.length > 0 && (
              <select value={project.created_by || ''} onChange={e => updateMeta({ owner: e.target.value })}
                title="RFP owner — reassign to another teammate"
                className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600 max-w-[180px]"
                data-testid="rfp-resp-owner-select">
                {!assignees.some(a => a.email === project.created_by) && (
                  <option value={project.created_by || ''}>{project.created_by || 'No owner'}</option>
                )}
                {assignees.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
              </select>
            )}
            <input type="number" min="0" step="1000" placeholder="Est. value"
              key={`${project.id}-${project.deal_value ?? ''}`} defaultValue={project.deal_value ?? ''}
              onBlur={e => {
                const v = e.target.value === '' ? null : Number(e.target.value);
                if (v !== (project.deal_value ?? null)) updateMeta({ deal_value: v === null ? -1 : v });
              }}
              title="Estimated deal value" className="h-7 w-24 text-[11px] border border-slate-200 rounded-md px-1.5 text-slate-600"
              data-testid="rfp-resp-value-input" />
            <select value={project.currency || 'AUD'} onChange={e => updateMeta({ currency: e.target.value })}
              title="Deal currency" className="h-7 text-[11px] border border-slate-200 rounded-md px-1 bg-white text-slate-600"
              data-testid="rfp-resp-currency-select">
              {['AUD', 'USD', 'EUR', 'GBP', 'NZD', 'CAD', 'SGD'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {canManage && (
                  <Button size="sm" variant="outline" className="h-7 text-xs px-2"
                    title="Assignment reminder settings"
                    onClick={() => { setReminderForm(project.reminder_settings || { enabled: true, days: 3 }); setReminderOpen(true); }}
                    data-testid="rfp-resp-reminder-settings-btn">
                    <BellRing className="w-3.5 h-3.5" />
                  </Button>
            )}
            </div>
          </div>
          <div className="border border-slate-200 bg-white rounded-md p-4 space-y-3" data-testid="rfp-container-pack">
            <div className="flex items-center gap-2 flex-wrap">
              <FileQuestion className="w-4 h-4 text-[#4ABFCF]" />
              <span className="text-sm font-semibold text-[#0F2040]">Client RFP documents</span>
              <span className="text-[10px] text-slate-500">Load the whole pack (ZIP or files) — Blueprint classifies every file and sheet, then you build the response shell</span>
              <span className="ml-auto flex items-center gap-1.5">
            <IntakeDropzone project={project} onStarted={() => openProject(project.id)} />
            {(project.documents || []).some(d => Array.isArray(d.questions)) && (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setStructureOpen(true)} title="Fix annexures, sections, question order, wording and numbering by hand" data-testid="rfp-structure-open-btn">
                <ListOrdered className="w-3.5 h-3.5 mr-1" />Fix structure
              </Button>
            )}
            {project.intake?.status === 'confirmed' && (
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setIntakeSummaryOpen(true)} data-testid="rfp-intake-summary-btn">
                <FileQuestion className="w-3.5 h-3.5 mr-1" />RFP pack ({project.intake.files.length})
              </Button>
            )}
              </span>
            </div>
          <IntakeReview project={project} onRefresh={() => openProject(project.id)} onBuilt={(recap) => setBuildRecap(recap)} />
          <TemplateSuggestion project={project} onApplied={() => openProject(project.id)} />
            {project.intake?.status === 'confirmed' && (
              <p className="text-[11px] text-slate-500" data-testid="rfp-container-shell-built">Response shell built from {project.intake.files.length} files on {project.intake.confirmed_at ? new Date(project.intake.confirmed_at).toLocaleDateString() : '—'}{project.intake.purpose ? ` — ${project.intake.purpose}` : ''}</p>
            )}
            {!project.intake && (project.documents || []).length === 0 && (
              <p className="text-[11px] text-slate-400" data-testid="rfp-container-empty">No RFP documents loaded yet. Use <b>Load RFP documents</b> above, or upload a single questionnaire on the Annexures tab.</p>
            )}
          </div>
          {project.outcome === 'won' && !starSuggHidden[project.id] && starSugg.length > 0 && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-1.5" data-testid="rfp-resp-star-suggestions">
              <div className="flex items-center gap-2">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-500 shrink-0" />
                <p className="text-amber-800 font-semibold flex-1">
                  This deal was won — star its {starSugg.length} top-scored answer{starSugg.length === 1 ? '' : 's'} as win stories so they get suggested first on similar future RFPs.
                </p>
                <Button size="sm" disabled={starSuggBusy} onClick={acceptStarSuggestions}
                  className="h-6 text-[10px] px-2 bg-amber-500 hover:bg-amber-600 text-white shrink-0"
                  data-testid="rfp-resp-star-all-btn">
                  {starSuggBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : `Star all ${starSugg.length}`}
                </Button>
                <button onClick={() => setStarSuggHidden(h => ({ ...h, [project.id]: true }))}
                  className="text-amber-300 hover:text-amber-600 shrink-0" title="Dismiss"
                  data-testid="rfp-resp-star-sugg-dismiss">
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              </div>
              {starSugg.map(s => (
                <p key={`${s.doc_id}-${s.idx}`} className="text-[10px] text-amber-700 truncate"
                  data-testid={`rfp-resp-star-sugg-${s.doc_id}-${s.idx}`}>
                  {s.client_score ? `[${s.client_score}] ` : ''}Q{s.idx + 1} · {s.question}
                </p>
              ))}
            </div>
          )}
          {project.outcome === 'lost' && (
            <div className="text-xs bg-rose-50 border border-rose-100 rounded-md px-3 py-2 space-y-1.5" data-testid="rfp-resp-loss-note">
              {project.loss_note && <p className="text-rose-700"><b>Why we lost:</b> {project.loss_note}</p>}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[9px] font-bold text-rose-400 uppercase tracking-wide">Loss reasons</span>
                {['pricing', 'feature gap', 'timing', 'relationship', 'compliance', 'other'].map(t => {
                  const cur = project.loss_reasons || [];
                  const on = cur.includes(t);
                  return (
                    <button key={t}
                      onClick={() => updateMeta({ loss_reasons: on ? cur.filter(x => x !== t) : [...cur, t] })}
                      className={`h-5 px-2 rounded-full border text-[10px] font-semibold transition-colors ${
                        on ? 'bg-rose-600 text-white border-rose-600'
                          : 'bg-white text-rose-500 border-rose-200 hover:border-rose-400'}`}
                      title={on ? 'Remove this loss reason' : 'Tag this loss reason'}
                      data-testid={`rfp-resp-loss-tag-${t.replace(' ', '-')}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {project.response_score && <ResponseScoreCard score={project.response_score} history={project.score_history} />}
          {(project.signoffs || []).length > 0 && (
            <div className="border border-slate-200 bg-white rounded-md p-3 space-y-1.5" data-testid="rfp-resp-signoffs">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Stakeholder sign-offs</p>
              {project.signoffs.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <Badge variant="outline" className={`text-[9px] shrink-0 ${s.decision === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    {s.decision === 'approved' ? 'Approved' : 'Changes requested'}
                  </Badge>
                  <span className="font-medium text-[#0F2040]">{s.name || 'Anonymous'}</span>
                  {s.comment && <span className="text-slate-500 min-w-0 flex-1">{s.comment}</span>}
                  <span className="text-[9px] text-slate-400 shrink-0">{s.at ? new Date(s.at).toLocaleDateString() : ''}</span>
                </div>
              ))}
            </div>
          )}
          </>)}
          {rfpTab === 'checklist' && (<>
          <GapTasksCard project={project} assignees={assignees} onRefresh={() => openProject(project.id)}
            onOpenDoc={id => { setRfpTab('annexures'); collapse.reveal(id); setTimeout(() => document.getElementById(`doc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); }}
            onOpenNarratives={(did, sid) => { if (did) setNarrFocus({ did, sid: sid || null, t: Date.now() }); setRfpTab('narratives'); }} />
          <ResponseShell project={project} assignees={assignees} onRefresh={() => openProject(project.id)}
            onOpenDoc={id => { setRfpTab('annexures'); collapse.reveal(id); setTimeout(() => document.getElementById(`doc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); }} />
          <PackageCard project={project} onRefresh={() => openProject(project.id)} />
          {!(project.deliverables || []).length && <p className="text-sm text-slate-400 py-10 text-center" data-testid="rfp-tab-empty">No checklist yet — load the client's RFP documents on the <b>Container</b> tab and build the response shell.</p>}
          </>)}
          {rfpTab === 'commercials' && <CommercialsCard project={project} onRefresh={() => openProject(project.id)} />}
          {rfpTab === 'narratives' && <NarrativesPanel project={project} assignees={assignees} onRefresh={() => openProject(project.id)} onOpenReviewers={() => setReviewersOpen(true)} focus={narrFocus} />}
          {rfpTab === 'annexures' && (<>
          <div className="flex items-center gap-2 flex-wrap" data-testid="rfp-annex-toolbar">
            <FileSpreadsheet className="w-4 h-4 text-[#4ABFCF]" />
            <span className="text-sm font-semibold text-[#0F2040]">Annexures</span>
            <span className="text-[10px] text-slate-500">One questionnaire per sheet, each with its own client scale</span>
            <span className="flex-1" />
            {(project.documents || []).length > 0 && (
              <button type="button" onClick={() => {
                const docs = {}; const secs = []; let n = 0;
                (project.documents || []).forEach(d => { const qs = d.questions || []; const mine = qs.filter(q => isMine(q, user?.email)); n += mine.length; docs[d.id] = mine.length === 0;
                  [...new Set(qs.map(q => q.section || ''))].forEach(sec => secs.push([d.id, sec, !qs.some(q => (q.section || '') === sec && isMine(q, user?.email))])); });
                collapse.applyLayout(docs, secs);
                toast.success(n ? `Showing the ${n} question${n === 1 ? '' : 's'} assigned to you — everything else is folded` : 'Nothing is assigned to you in this RFP — everything is folded', { id: 'only-mine' });
              }} className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#0F2040]" title="Open only the annexures and sections with questions assigned to you; fold the rest" data-testid="rfp-annex-only-mine"><UserRound className="w-3 h-3" />Only mine</button>
            )}
            {(project.documents || []).length > 0 && (
              <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer" title="Automatically fold annexures and sections where every answer is accepted, so only live work stays open. Open one by hand and it stays open." data-testid="rfp-annex-smart-fold-label">
                <input type="checkbox" className="accent-[#0F2040]" checked={collapse.smart} onChange={e => collapse.setSmart(e.target.checked)} data-testid="rfp-annex-smart-fold" />
                Fold finished
              </label>
            )}
            {(project.documents || []).length > 1 && (
              <span className="inline-flex items-center gap-1" data-testid="rfp-annex-collapse-controls">
                <button type="button" onClick={() => collapse.setAllDocs((project.documents || []).map(d => d.id), true)} className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#0F2040]" title="Fold every annexure down to its header line" data-testid="rfp-annex-collapse-all"><ChevronsDownUp className="w-3 h-3" />Collapse all</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={() => collapse.setAllDocs((project.documents || []).map(d => d.id), false)} className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#0F2040]" title="Open every annexure" data-testid="rfp-annex-expand-all"><ChevronsUpDown className="w-3 h-3" />Expand all</button>
              </span>
            )}
            {(project.documents || []).some(d => Array.isArray(d.questions)) && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setStructureOpen(true)}
                title="Fix annexures, sections, question order, wording and numbering by hand" data-testid="rfp-structure-open-btn-annexures">
                <ListOrdered className="w-3.5 h-3.5 mr-1" />Fix structure
              </Button>
            )}
            {anyProcessed && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={rematchAll}
                  title="Re-run library matching for every document — answers, flags and assignments are kept"
                  data-testid="rfp-resp-rematch-all-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />Re-match all
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => exportFile('xlsx')} disabled={exporting}
                  data-testid="rfp-resp-export-btn">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />}Export Excel
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => exportFile('docx')} disabled={exporting}
                  data-testid="rfp-resp-export-docx-btn">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}Export Word
                </Button>
                <label className="flex items-center gap-1 text-[10px] text-slate-500"
                  title="Quality gate — exporting warns when any answer scores below this bar">
                  Q bar
                  <input type="number" min="0" max="98" value={qualityBar}
                    onChange={e => { const v = Math.max(0, Math.min(98, Number(e.target.value))); setQualityBar(v); localStorage.setItem('rfpQualityBar', String(v)); }}
                    className="h-7 w-12 text-[11px] border border-slate-200 rounded-md px-1 text-slate-600"
                    data-testid="rfp-resp-quality-bar-input" />
                </label>
              </>
            )}
            <select value={qCol} onChange={e => setQCol(e.target.value)}
              title="Preferred question column for spreadsheets — you can still confirm/change it in the preview before upload"
              className="h-7 text-[11px] border border-slate-200 rounded-md px-1 bg-white text-slate-600"
              data-testid="rfp-resp-qcol-select">
              <option value="auto">Questions: auto</option>
              {'ABCDEFGHIJ'.split('').map(c => <option key={c} value={c}>{`Questions: col ${c}`}</option>)}
            </select>
            <Dialog open={!!colMap} onOpenChange={v => { if (!v) { setColMap(null); if (fileRef.current) fileRef.current.value = ''; } }}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rfp-colmap-dialog">
                <DialogHeader>
                  <DialogTitle className="text-sm">Which column holds the client questions?</DialogTitle>
                </DialogHeader>
                {!colMap?.columns ? (
                  <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF] mx-auto my-6" />
                ) : (
                  <div className="space-y-2">
                    <Button size="sm" variant="outline" className="h-7 text-xs w-full justify-start"
                      onClick={() => doUpload(colMap.file, '')} disabled={uploading} data-testid="rfp-colmap-auto">
                      Auto — scan the whole sheet
                    </Button>
                    {colMap.columns.map(c => (
                      <button key={c.letter} onClick={() => doUpload(colMap.file, c.letter)} disabled={uploading}
                        className={`w-full text-left border rounded-md p-2 hover:border-[#4ABFCF] transition-colors ${qCol === c.letter ? 'border-[#4ABFCF] bg-[#4ABFCF]/5' : 'border-slate-200'}`}
                        data-testid={`rfp-colmap-col-${c.letter}`}>
                        <p className="text-xs font-bold text-[#0F2040]">Column {c.letter}{qCol === c.letter ? ' — your preferred pick' : ''}</p>
                        {c.samples.map((s, i) => <p key={i} className="text-[10px] text-slate-500 truncate">· {s}</p>)}
                      </button>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>
            <label className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#0F2040] hover:bg-[#1A3055] rounded-md px-3 py-1.5 cursor-pointer"
              data-testid="rfp-resp-upload-label">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
              Upload RFP document
              <input ref={fileRef} type="file" accept=".docx,.pdf,.txt,.md,.xls,.xlsx" className="hidden" disabled={uploading}
                onChange={e => upload(e.target.files?.[0])} data-testid="rfp-resp-file-input" />
            </label>
          </div>
          <ClientScoringPanel project={project}
            scoreFilter={qfScore} onScoreFilter={setQfScore}
            onScheme={s => setProject(prev => {
              const next = { ...prev };
              if (s) next.scoring_scheme = s; else delete next.scoring_scheme;
              return next;
            })}
            onRefresh={() => openProject(project.id)} />
          {(project.documents || []).some(d => (d.questions || []).some(q => q.auto_drafted)) && (
            <AutoDraftReviewQueue project={project} onRefresh={() => openProject(project.id)} />
          )}
          {(project.documents || []).length === 0 && (
            <p className="text-sm text-slate-400 py-8 text-center" data-testid="rfp-resp-nodocs">Upload the client's Word / Excel / PDF question documents to start matching.</p>
          )}
          {Object.keys(selQs).length > 0 && (
            <div className="sticky top-2 z-20 flex items-center gap-2 bg-[#0F2040] text-white rounded-md px-3 py-2 shadow-md"
              data-testid="rfp-bulk-assign-bar">
              <span className="text-xs font-semibold" data-testid="rfp-bulk-count">{Object.keys(selQs).length} selected</span>
              <select value={bulkAssignee} onChange={e => setBulkAssignee(e.target.value)}
                className="h-7 text-[11px] rounded-md px-1.5 bg-white text-slate-700 max-w-[200px]"
                data-testid="rfp-bulk-assignee-select">
                <option value="">Assign to…</option>
                {assignees.map(a => <option key={a.email} value={a.email}>{`${a.email} · ${a.open_count || 0} open`}</option>)}
              </select>
              <select value={bulkStrength} onChange={e => setBulkStrength(e.target.value)}
                className="h-7 text-xs border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                data-testid="rfp-bulk-strength-select">
                <option value="">Set match strength…</option>
                <option value="strong">strong</option>
                <option value="partial">partial</option>
                <option value="none">Gap</option>
              </select>
              <select value={bulkFlag} onChange={e => setBulkFlag(e.target.value)}
                className="h-7 text-xs border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                data-testid="rfp-bulk-flag-select">
                <option value="">Set review status…</option>
                {Object.entries(FLAG_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <Button size="sm" className="h-7 text-[11px] bg-[#4ABFCF] hover:bg-[#3AA9B9] text-white px-3"
                title="Applies whichever of assignee, strength and status you've picked — all in one pass"
                onClick={bulkApplyCombo} data-testid="rfp-bulk-apply-btn">Apply</Button>
              <span className="text-slate-300">|</span>
              <select value={selectedCombo && comboPresets.some(p => p.id === selectedCombo) ? selectedCombo : ''}
                onChange={e => { const p = comboPresets.find(x => x.id === e.target.value); if (p) applyComboPreset(p); else setSelectedCombo(''); }}
                title="Load a saved bulk combo into the selects, then Apply"
                className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                data-testid="rfp-bulk-combo-select">
                <option value="">Saved combos…</option>
                {comboPresets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {selectedCombo && comboPresets.some(p => p.id === selectedCombo) && (
                <button type="button" onClick={() => deleteComboPreset(selectedCombo)}
                  title="Delete this saved combo" className="text-[10px] text-rose-500 hover:underline"
                  data-testid="rfp-bulk-combo-del">delete</button>
              )}
              {comboSaveOpen ? (
                <span className="flex items-center gap-1">
                  <input value={comboName} onChange={e => setComboName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveComboPreset(); }}
                    placeholder="Combo name…" autoFocus
                    className="h-7 text-[11px] border border-[#4ABFCF] rounded-md px-2 w-[110px] focus:outline-none"
                    data-testid="rfp-bulk-combo-name" />
                  <button type="button" onClick={saveComboPreset} className="text-[10px] font-semibold text-[#0F2040] hover:underline"
                    data-testid="rfp-bulk-combo-save">Save</button>
                  <button type="button" onClick={() => { setComboSaveOpen(false); setComboName(''); }}
                    className="text-[10px] text-slate-400 hover:underline">cancel</button>
                </span>
              ) : (
                <button type="button" onClick={() => setComboSaveOpen(true)}
                  title="Save the current assignee/strength/status picks as a reusable combo"
                  className="text-[10px] font-semibold text-[#4ABFCF] hover:underline"
                  data-testid="rfp-bulk-combo-save-open">Save combo</button>
              )}
              <button onClick={() => setSelQs({})} className="text-[11px] text-slate-300 hover:text-white ml-auto"
                data-testid="rfp-bulk-clear">Clear</button>
            </div>
          )}
          {(project.documents || []).map(d => { const qs = d.questions || []; const docDone = d.status === 'built' && allAccepted(qs, DONE_FLAGS_UI); const folded = collapse.isDocCollapsed(d.id, docDone); return (
            <Card key={d.id} id={`doc-${d.id}`} className={`border-slate-200 ${folded ? 'bg-slate-50/50' : ''}`} data-testid={`rfp-resp-doc-${d.id}`} data-collapsed={folded ? 'true' : 'false'}>
              <CardContent className={`p-4 ${folded ? '' : 'space-y-3'}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={() => collapse.toggleDoc(d.id, folded)} className="text-slate-400 hover:text-[#0F2040] shrink-0" title={folded ? 'Expand this annexure' : 'Collapse this annexure to one line'} aria-expanded={!folded} data-testid={`rfp-resp-doc-toggle-${d.id}`}>
                    {folded ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <span className="text-sm font-semibold text-[#0F2040] cursor-pointer" onClick={() => collapse.toggleDoc(d.id, folded)}>{d.filename}</span>
                  {docDone && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" title="Every answer in this annexure is accepted" data-testid={`rfp-resp-doc-done-${d.id}`}><CheckCircle2 className="w-2.5 h-2.5 mr-1" />all accepted</Badge>}
                  {folded && d.status === 'built' && qs.length > 0 && (
                    <span className="text-[10px] text-slate-500" data-testid={`rfp-resp-doc-folded-summary-${d.id}`}>
                      {qs.length} question{qs.length === 1 ? '' : 's'} · {qs.filter(q => q.response).length} answered · {qs.filter(q => !q.response && !q.is_pricing).length} open · {qs.filter(q => q.response && DONE_FLAGS_UI.includes(q.flag)).length} accepted
                    </span>
                  )}
                  {d.source?.sheet && <span className="text-[10px] text-slate-400" title={d.source.filename}>sheet “{d.source.sheet}”</span>}
                  {d.source?.file_id && (
                    <a href={`${API_URL}/api/rfp/response/projects/${project.id}/intake/files/${d.source.file_id}/writeback`}
                      className="text-[10px] text-[#4ABFCF] hover:underline" title={`Download the client's original workbook (${d.source.filename}) with our scores and comments written into their rows`}
                      data-testid={`rfp-resp-doc-writeback-${d.id}`}>client workbook ↓</a>
                  )}
                  {(() => { const eff = d.scoring_scheme?.levels?.length ? d.scoring_scheme.levels : (project.scoring_scheme?.levels || []); const own = !!d.scoring_scheme?.levels?.length; return (
                    <button type="button" onClick={() => setScaleDoc(d)}
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-medium transition-colors ${own ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : eff.length ? 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100' : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'}`}
                      title={own ? `This annexure's own client scale (${d.scoring_scheme.source || 'detected'}) — click to edit:\n` + eff.map(l => `${l.label} — ${l.definition}`).join('\n') : eff.length ? 'Following the project default scale — click to give this annexure its own' : 'No client scale yet — click to add one for this annexure'}
                      data-testid={`rfp-resp-doc-scale-${d.id}`}>
                      {own ? `scale ${eff.map(l => l.label).join(' / ')}` : eff.length ? `default scale ${eff.map(l => l.label).join(' / ')}` : 'set scale'}
                    </button>
                  ); })()}
                  {(() => { const mm = docScaleMismatch(project, d); const rm = (d.questions || []).filter(q => q.client_score_remapped).length; return mm.rows > 0 ? (
                    <button type="button" onClick={() => setRemapDoc(d)}
                      className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800 px-2 py-0.5 text-[9px] font-medium hover:bg-amber-100 transition-colors"
                      title={`Scores not on this annexure's scale: ${mm.mismatched.map(x => `${x.score} (${x.count})`).join(', ')} — click to remap or AI re-score`}
                      data-testid={`rfp-resp-doc-scale-mismatch-${d.id}`}>
                      <AlertTriangle className="w-3 h-3" />{mm.rows} score{mm.rows === 1 ? '' : 's'} off scale · remap
                    </button>
                  ) : rm > 0 ? (
                    <button type="button" onClick={() => setRemapDoc(d)}
                      className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 px-2 py-0.5 text-[9px] font-medium hover:bg-indigo-100 transition-colors"
                      title="These scores were remapped by nearest number — let AI re-score them against the scale definitions"
                      data-testid={`rfp-resp-doc-scale-remapped-${d.id}`}>
                      <Sparkles className="w-3 h-3" />{rm} remapped · AI re-score
                    </button>
                  ) : null; })()}
                  {d.weights?.length > 0 && (
                    <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200"
                      title={d.weights.map(w => `${w.category}: ${Math.round(w.weight * 100)}%`).join('\n')} data-testid={`rfp-resp-doc-weights-${d.id}`}>
                      {d.weights.length} weighted categories
                    </Badge>
                  )}
                  {BUSY.includes(d.status) ? (() => {
                    const hb = d.progress?.updated_at;
                    const stalled = hb ? (Date.now() - new Date(hb).getTime() > 4 * 60 * 1000) : false;
                    return stalled ? (
                      <>
                        <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200"
                          title="No progress for over 4 minutes — likely interrupted by a restart or a hung AI call"
                          data-testid={`rfp-resp-doc-stalled-${d.id}`}>
                          Stalled{d.progress?.pct != null ? ` at ${d.progress.pct}%` : ''} — {d.progress?.label || d.status}
                        </Badge>
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                          onClick={() => reparseDoc(d.id)} data-testid={`rfp-resp-doc-restart-${d.id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" />Restart parse
                        </Button>
                      </>
                    ) : (
                      <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200"
                        data-testid={`rfp-resp-doc-progress-${d.id}`}>
                        <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />
                        {d.progress?.label
                          || (d.status === 'extracting' ? 'Extracting questions…' : d.status === 'matching' ? `Matching ${d.question_count || ''} questions…` : 'Drafting responses…')}
                        {d.progress?.pct != null ? ` ${d.progress.pct}%` : ''}
                        {d.progress?.eta_seconds > 0 ? ` · ~${d.progress.eta_seconds < 60 ? Math.max(5, Math.round(d.progress.eta_seconds / 5) * 5) + 's' : Math.ceil(d.progress.eta_seconds / 60) + 'm'} left` : ''}
                      </Badge>
                    );
                  })() : d.status === 'failed' ? (
                    <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">Failed: {d.error}</Badge>
                  ) : d.status === 'empty' ? (
                    <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500">No questions found</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`rfp-resp-coverage-${d.id}`}>
                      {d.scanned_rows ? `${d.scanned_rows} rows scanned · ` : ''}{d.question_count} questions found
                    </Badge>
                  )}
                  {(() => {
                    const ds = (project.response_score?.by_document || []).find(x => x.doc_id === d.id);
                    return ds ? (
                      <Badge variant="outline" className="text-[9px] bg-[#4ABFCF]/10 text-[#0F2040] border-[#4ABFCF]/40" data-testid={`rfp-resp-docscore-${d.id}`}>
                        <Gauge className="w-2.5 h-2.5 mr-1 text-[#4ABFCF]" />confidence {ds.confidence_score}% · capability {ds.capability_score}%
                      </Badge>
                    ) : null;
                  })()}
                  {['ready', 'built', 'empty', 'failed'].includes(d.status) && (
                    <span className="flex items-center gap-1 ml-auto">
                      <Button size="sm" variant="outline" onClick={() => reparseDoc(d.id)}
                        title="Re-parse this document with the latest extraction logic — answers for unchanged questions carry over"
                        className="h-6 text-[10px] px-2" data-testid={`rfp-resp-reparse-btn-${d.id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" />Re-parse
                      </Button>
                      {['ready', 'built'].includes(d.status) && (
                        <>
                      <Button size="sm" variant="outline" onClick={() => rematchDoc(d.id)}
                        title="Re-match all questions against the current Capability Library (answers, flags and assignments are kept), then refresh scores"
                        className="h-6 text-[10px] px-2" data-testid={`rfp-resp-rematch-btn-${d.id}`}>
                        <RefreshCw className="w-3 h-3 mr-1" />Re-match
                      </Button>
                      <label className="inline-flex items-center gap-1 h-6 text-[10px] px-2 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 text-slate-700"
                        title="Upload an updated version of this client document (e.g. question clarifications) — answers carry over for unchanged questions"
                        data-testid={`rfp-resp-replace-label-${d.id}`}>
                        <FileDiff className="w-3 h-3" />Update doc
                        <input type="file" accept=".docx,.pdf,.txt,.md,.xls,.xlsx" className="hidden"
                          onChange={e => replaceDoc(d.id, e.target.files?.[0])} data-testid={`rfp-resp-replace-input-${d.id}`} />
                      </label>
                      {d.status === 'built' && d.scoring_scheme?.extra_columns?.length > 0 && (
                        <Button size="sm" variant="outline" onClick={() => matrixFillDoc(d.id)}
                          title={`AI drafts the client's matrix columns (${d.scoring_scheme.extra_columns.join(', ')}) for every answered row that has none yet`}
                          className="h-6 text-[10px] px-2 text-violet-700 border-violet-200 hover:bg-violet-50" data-testid={`rfp-resp-matrixfill-btn-${d.id}`}>
                          <Sparkles className="w-3 h-3 mr-1" />Auto-fill columns
                        </Button>
                      )}
                      {d.status === 'ready' && (
                        <Button size="sm" onClick={() => buildDoc(d.id)}
                          className="h-6 text-[10px] px-2 bg-[#4ABFCF] hover:bg-[#3AA9B9] text-white" data-testid={`rfp-resp-build-btn-${d.id}`}>
                          <Hammer className="w-3 h-3 mr-1" />Build Response
                        </Button>
                      )}
                      {d.status === 'built' && (d.questions || []).some(q => !q.response && !q.is_pricing) && (
                        <Button size="sm" variant="outline" onClick={() => autoDraftDoc(d.id)}
                          title="Draft every unanswered row from the best-matching narrative paragraph and library block (rows without a source are left for gap tasks)"
                          className="h-6 text-[10px] px-2 text-indigo-700 border-indigo-200 hover:bg-indigo-50" data-testid={`rfp-resp-autodraft-btn-${d.id}`}>
                          <Sparkles className="w-3 h-3 mr-1" />Auto-draft {(d.questions || []).filter(q => !q.response && !q.is_pricing).length} rows
                        </Button>
                      )}
                      {d.auto_draft && d.status === 'built' && (
                        <span className="text-[10px] text-slate-400" title={`Last auto-draft ${new Date(d.auto_draft.at).toLocaleString()} by ${d.auto_draft.by}`} data-testid={`rfp-resp-autodraft-info-${d.id}`}>auto-drafted {d.auto_draft.drafted} · skipped {d.auto_draft.skipped}</span>
                      )}
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteAnnexure(d)}
                        title="Delete this annexure — it goes to the bin below and can be restored"
                        className="h-6 text-[10px] px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`rfp-resp-delete-annexure-${d.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />Delete
                      </Button>
                    </span>
                  )}
                </div>
                {!folded && (<>
                {['ready', 'built'].includes(d.status) && d.scores && <ScorePanel scores={d.scores} />}
                {['ready', 'built'].includes(d.status) && d.summary && (
                  <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-md p-2.5 leading-relaxed" data-testid={`rfp-resp-summary-${d.id}`}>
                    <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500 -mt-px" />{d.summary}
                  </p>
                )}
                {d.status === 'built' && <ResponseProgress questions={d.questions || []} />}
                {d.status === 'built' && (d.questions || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-2" data-testid={`rfp-resp-qfilter-${d.id}`}>
                    <input type="text" value={qfSearch} onChange={e => setQfSearch(e.target.value)}
                      placeholder="Search question or answer…"
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-2 bg-white text-slate-600 w-[190px] focus:outline-none focus:border-[#4ABFCF]"
                      data-testid={`rfp-resp-qfilter-search-${d.id}`} />
                    <select value={qfStatus} onChange={e => setQfStatus(e.target.value)}
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                      data-testid={`rfp-resp-qfilter-status-${d.id}`}>
                      <option value="all">All statuses</option>
                      {Object.entries(FLAG_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select value={qfAssignee} onChange={e => setQfAssignee(e.target.value)}
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600 max-w-[170px]"
                      data-testid={`rfp-resp-qfilter-assignee-${d.id}`}>
                      <option value="all">All assignees</option>
                      <option value="unassigned">Unassigned</option>
                      {assignees.map(a => <option key={a.email} value={a.email}>{a.email}</option>)}
                    </select>
                    <select value={qfMatch} onChange={e => setQfMatch(e.target.value)}
                      title="Filter by match strength or pricing-flagged questions"
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                      data-testid={`rfp-resp-qfilter-match-${d.id}`}>
                      <option value="all">All matches</option>
                      <option value="strong">Strong</option>
                      <option value="partial">Partial</option>
                      <option value="none">Gap</option>
                      <option value="pricing">Pricing</option>
                    </select>
                    <select value={qfConf} onChange={e => setQfConf(e.target.value)}
                      title="Filter by AI confidence in the drafted answer"
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                      data-testid={`rfp-resp-qfilter-conf-${d.id}`}>
                      <option value="all">Any confidence</option>
                      <option value="high">Confidence: high</option>
                      <option value="med">Confidence: med</option>
                      <option value="low">Confidence: low</option>
                    </select>
                    <select value={qfFit} onChange={e => setQfFit(e.target.value)}
                      title="Filter by how well the answer fits the question"
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                      data-testid={`rfp-resp-qfilter-fit-${d.id}`}>
                      <option value="all">Any fit</option>
                      <option value="high">Fit: high</option>
                      <option value="med">Fit: med</option>
                      <option value="low">Fit: low</option>
                    </select>
                    <select value={appliedPreset && presets.some(p => p.id === appliedPreset) ? appliedPreset : ''}
                      onChange={e => { const p = presets.find(x => x.id === e.target.value); if (p) applyPreset(p); else setAppliedPreset(''); }}
                      title="Reapply a saved filter combination in one click"
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                      data-testid={`rfp-resp-qfilter-presets-${d.id}`}>
                      <option value="">Saved filters…</option>
                      {presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    {appliedPreset && presets.some(p => p.id === appliedPreset) && (
                      <>
                        <button type="button" onClick={() => toggleDefault(appliedPreset)}
                          title="Your default filter applies automatically when you open a project"
                          className={`text-[10px] hover:underline ${presets.find(p => p.id === appliedPreset)?.is_default ? 'text-[#0F2040] font-semibold' : 'text-slate-400'}`}
                          data-testid={`rfp-resp-qfilter-preset-default-${d.id}`}>
                          {presets.find(p => p.id === appliedPreset)?.is_default ? '★ default' : '☆ make default'}
                        </button>
                        <button type="button" onClick={() => deletePreset(appliedPreset)}
                          title="Delete this saved filter"
                          className="text-[10px] text-rose-500 hover:underline"
                          data-testid={`rfp-resp-qfilter-preset-del-${d.id}`}>delete</button>
                      </>
                    )}
                    {presetSaveOpen ? (
                      <span className="flex items-center gap-1">
                        <input value={presetName} onChange={e => setPresetName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') savePreset(); }}
                          placeholder="Filter name…" autoFocus
                          className="h-7 text-[11px] border border-[#4ABFCF] rounded-md px-2 w-[110px] focus:outline-none"
                          data-testid={`rfp-resp-qfilter-preset-name-${d.id}`} />
                        <button type="button" onClick={savePreset} className="text-[10px] font-semibold text-[#0F2040] hover:underline"
                          data-testid={`rfp-resp-qfilter-preset-save-${d.id}`}>Save</button>
                        <button type="button" onClick={() => { setPresetSaveOpen(false); setPresetName(''); }}
                          className="text-[10px] text-slate-400 hover:underline">cancel</button>
                      </span>
                    ) : (
                      <button type="button" onClick={() => setPresetSaveOpen(true)}
                        title="Save the current filter combination for one-click reuse"
                        className="text-[10px] font-semibold text-[#4ABFCF] hover:underline"
                        data-testid={`rfp-resp-qfilter-save-${d.id}`}>Save filter</button>
                    )}
                    {(d.scoring_scheme?.levels?.length > 0 || project.scoring_scheme?.levels?.length > 0) && (
                      <select value={qfScore} onChange={e => setQfScore(e.target.value)}
                        className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
                        data-testid={`rfp-resp-qfilter-score-${d.id}`}>
                        <option value="all">All client scores</option>
                        <option value="unscored">Unscored</option>
                        {(d.scoring_scheme?.levels?.length ? d.scoring_scheme.levels : project.scoring_scheme.levels).map(lv => (
                          <option key={lv.label} value={lv.label}>Score {lv.label}</option>
                        ))}
                      </select>
                    )}
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                      <input type="checkbox" className="accent-[#0F2040]" checked={qfGaps}
                        onChange={e => setQfGaps(e.target.checked)} data-testid={`rfp-resp-qfilter-gaps-${d.id}`} />
                      Gaps only
                    </label>
                    <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer"
                      title="Sort answered questions by quality score so the weakest drafts come first">
                      <input type="checkbox" className="accent-[#0F2040]" checked={qfWeakest}
                        onChange={e => setQfWeakest(e.target.checked)} data-testid={`rfp-resp-qfilter-weakest-${d.id}`} />
                      Weakest first
                    </label>
                    <span className="text-[10px] text-slate-400" data-testid={`rfp-resp-qfilter-count-${d.id}`}>
                      showing {filterQuestions(d.questions || [], d.id).length} of {(d.questions || []).length}
                    </span>
                    {(() => { const secs = [...new Set((d.questions || []).map(q => q.section || ''))]; return secs.length > 1 ? (
                      <span className="inline-flex items-center gap-1 ml-auto" data-testid={`rfp-resp-sections-controls-${d.id}`}>
                        <button type="button" onClick={() => collapse.setAllSecs(d.id, secs, true)} className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#0F2040]" title="Fold every section in this annexure" data-testid={`rfp-resp-sections-collapse-all-${d.id}`}><ChevronsDownUp className="w-3 h-3" />Collapse sections</button>
                        <span className="text-slate-300">·</span>
                        <button type="button" onClick={() => collapse.setAllSecs(d.id, secs, false)} className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#0F2040]" title="Open every section in this annexure" data-testid={`rfp-resp-sections-expand-all-${d.id}`}><ChevronsUpDown className="w-3 h-3" />Expand sections</button>
                      </span>
                    ) : null; })()}
                  </div>
                )}
                {d.status === 'built' ? (
                  <div className="space-y-2.5" data-testid={`rfp-resp-questions-${d.id}`}>
                    {filterQuestions(d.questions || [], d.id).map((q, qi, arr) => { const hasSecs = (d.questions || []).some(x => x.section); const inSec = arr.filter(x => (x.section || '') === (q.section || '')); const secDone = hasSecs && allAccepted(inSec, DONE_FLAGS_UI); const secFolded = hasSecs && collapse.isSecCollapsed(d.id, q.section, secDone); const isHead = (qi === 0 || (arr[qi - 1].section || '') !== (q.section || '')) && hasSecs; if (secFolded && !isHead) return null; return (
                      <div key={q.idx} className="space-y-1">
                        {isHead && (
                          <div className="flex items-center gap-2 pt-2" data-testid={`rfp-resp-section-${d.id}-${q.idx}`} data-collapsed={secFolded ? 'true' : 'false'}>
                            <button type="button" onClick={() => collapse.toggleSec(d.id, q.section, secFolded)} className="text-slate-400 hover:text-[#0F2040] shrink-0" title={secFolded ? 'Expand this section' : 'Collapse this section'} aria-expanded={!secFolded} data-testid={`rfp-resp-section-toggle-${d.id}-${q.idx}`}>
                              {secFolded ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                            <span className={`text-[11px] font-semibold uppercase tracking-wide cursor-pointer ${q.section ? 'text-[#0F2040]' : 'text-slate-400 italic'}`} onClick={() => collapse.toggleSec(d.id, q.section, secFolded)}>{q.section || 'No section'}</span>
                            <span className="text-[10px] text-slate-400" data-testid={`rfp-resp-section-count-${d.id}-${q.idx}`}>{inSec.length} question{inSec.length === 1 ? '' : 's'}{secFolded ? ` · ${inSec.filter(x => x.response).length} answered · ${inSec.filter(x => !x.response && !x.is_pricing).length} open` : ''}</span>
                            {secDone && <span className="inline-flex items-center gap-0.5 text-[9px] text-emerald-700" title="Every answer in this section is accepted" data-testid={`rfp-resp-section-done-${d.id}-${q.idx}`}><CheckCircle2 className="w-2.5 h-2.5" />all accepted</span>}
                            <span className="flex-1 border-t border-slate-200" />
                          </div>
                        )}
                        {secFolded ? null : (<>
                        {keptQs[`${d.id}:${q.idx}`] && !matchesFilters(q) && (
                          <div className="flex items-center gap-2 text-[10px] text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-md px-2 py-1"
                            data-testid={`rfp-resp-kept-${d.id}-${q.idx}`}>
                            <span className="min-w-0 flex-1">Updated — this question no longer matches the current filters. It stays here until you hide it.</span>
                            <button type="button"
                              onClick={() => setKeptQs(prev => { const n = { ...prev }; delete n[`${d.id}:${q.idx}`]; return n; })}
                              className="shrink-0 font-semibold text-[#0F2040] hover:underline"
                              data-testid={`rfp-resp-kept-hide-${d.id}-${q.idx}`}>Hide</button>
                          </div>
                        )}
                        <ResponseQuestionRow q={q} docId={d.id} assignees={assignees} onUpdate={updateQuestion}
                          canManage={canManage} onToLibrary={addToLibrary} onPromote={promoteToLibrary}
                          scaleLevels={d.scoring_scheme?.levels?.length ? d.scoring_scheme.levels : project.scoring_scheme?.levels}
                          extraColumns={d.scoring_scheme?.extra_columns}
                          projectId={project.id}
                          onBrowseStories={(docId, idx, question) => { setInsertTarget({ docId, idx, question }); setWinStoriesOpen(true); }}
                          onFromNarrative={(docId, idx, question) => setNarrTarget({ docId, idx, question })}
                          selected={!!selQs[`${d.id}:${q.idx}`]} onToggleSelect={() => toggleSelQ(d.id, q.idx)} />
                        </>)}
                      </div>
                    ); })}
                    {filterQuestions(d.questions || [], d.id).length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-4" data-testid={`rfp-resp-qfilter-nomatch-${d.id}`}>
                        No questions match the current filters.
                      </p>
                    )}
                  </div>
                ) : d.status === 'ready' && (
                  <div className="border border-slate-100 rounded-md divide-y divide-slate-50 max-h-96 overflow-y-auto" data-testid={`rfp-resp-questions-${d.id}`}>
                    {(d.questions || []).map(q => (
                      <div key={q.idx} className="px-3 py-2 flex items-start gap-2" data-testid={`rfp-resp-q-${d.id}-${q.idx}`}>
                        <Badge variant="outline" className={`text-[8px] mt-0.5 shrink-0 ${q.is_pricing ? 'bg-sky-50 text-sky-700 border-sky-200' : STRENGTH[q.strength]}`}>
                          {q.is_pricing ? 'Pricing — review' : q.strength === 'none' ? 'Gap' : q.strength}
                        </Badge>
                        <div className="min-w-0">
                          <p className="text-xs text-[#0F2040]">{q.no && <span className="font-mono text-[10px] text-slate-400 mr-1">{q.no}</span>}{q.title && <span className="text-[#4ABFCF] mr-1">{q.title} —</span>}{q.text}
                            {q.cluster_id && <span className="ml-1.5 inline-flex items-center text-[9px] text-violet-600" title="Overlaps requirements in other sections — answered once, reused everywhere" data-testid={`rfp-resp-linked-${d.id}-${q.idx}`}><Link2 className="w-2.5 h-2.5 mr-0.5" />linked</span>}
                            {q.client_score && q.client_score_source === 'boilerplate' && (
                              <span className={`ml-1.5 inline-flex items-center rounded-full border px-1.5 text-[9px] ${q.client_score_reviewed ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-violet-50 text-violet-700 border-violet-200'}`}
                                title={`${q.client_score_reviewed ? 'Confirmed' : 'Suggested from the matched boilerplate'}: ${q.client_score_rationale || ''}`} data-testid={`rfp-resp-mode-${d.id}-${q.idx}`}>
                                {q.client_score_reviewed ? '' : 'suggested · '}{q.client_score}
                              </span>
                            )}
                          </p>
                          {q.match && (
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              → <span className="font-mono">{q.match.capability_id}</span> {q.match.title}
                              {q.match.category_names?.length ? ` · ${q.match.category_names.join(', ')}` : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </>)}
              </CardContent>
            </Card>
          ); })}
          <DeletedAnnexures project={project} onRefresh={() => openProject(project.id)} />
          </>)}
          {rfpTab === 'attachments' && <AttachmentsPanel project={project} assignees={assignees} onRefresh={() => openProject(project.id)} />}
          {rfpTab === 'history' && <HistoryPanel project={project} onRefresh={() => openProject(project.id)} />}
          {rfpTab === 'preview' && (<>
            <div className="flex items-center gap-2 flex-wrap" data-testid="rfp-preview-toolbar">
              <Eye className="w-4 h-4 text-[#4ABFCF]" />
              <span className="text-sm font-semibold text-[#0F2040]">Preview</span>
              <span className="text-[10px] text-slate-500">Every document as the client will receive it</span>
              <span className="flex-1" />
              {project.preview_token && <span className="text-[10px] text-cyan-700" data-testid="rfp-preview-share-live">Preview link live · opened {project.preview_view_count || 0}×</span>}
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPreviewShareOpen(true)} title="Send a read-only preview link to a colleague or the client sponsor" data-testid="rfp-preview-share-btn">
                <Share2 className="w-3.5 h-3.5 mr-1" />{project.preview_token ? 'Preview link' : 'Share preview'}
              </Button>
            </div>
            <PreviewPanel project={project} onJump={jumpToGap} assignees={assignees} onAssign={assignGap} onSeen={(at) => setProject((prev) => (prev ? { ...prev, preview_seen_at: at } : prev))} />
            <PreviewShareDialog project={project} open={previewShareOpen} onOpenChange={setPreviewShareOpen} onChanged={(patch) => setProject((prev) => (prev ? { ...prev, ...patch } : prev))} />
          </>)}
          <IntakeSummaryDialog project={project} open={intakeSummaryOpen} onOpenChange={setIntakeSummaryOpen} />
          <StructureEditor project={project} open={structureOpen} onOpenChange={setStructureOpen} onChanged={() => openProject(project.id)} />
          <Dialog open={nudgeOpen} onOpenChange={setNudgeOpen}>
            <DialogContent className="max-w-sm" data-testid="rfp-resp-nudge-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-sm"><Bell className="w-4 h-4 text-[#4ABFCF]" />Step nudges — {project.name}</DialogTitle>
              </DialogHeader>
              <p className="text-xs text-slate-500">If this RFP sits on the same step (Load → Sort → Shape → Preview → Package) for too long before its due date, the owner gets a bell and an email — once per step.</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600">Nudge after</span>
                <div className="flex gap-1" data-testid="rfp-resp-nudge-days">
                  {[2, 3, 5].map((n) => (
                    <button key={n} type="button" disabled={nudgeForm.muted} onClick={() => setNudgeForm((f) => ({ ...f, days: n }))} className={`h-7 px-3 text-xs rounded-md border ${nudgeForm.days === n ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'} disabled:opacity-40`} data-testid={`rfp-resp-nudge-days-${n}`}>{n} days</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600">Mute nudges for this RFP</span>
                <Switch checked={nudgeForm.muted} onCheckedChange={(v) => setNudgeForm((f) => ({ ...f, muted: v }))} data-testid="rfp-resp-nudge-mute-switch" />
              </div>
              {project.step_state?.step && project.step_state.step !== 'done' && <p className="text-[10px] text-slate-400" data-testid="rfp-resp-nudge-state">Currently on “{project.step_state.step}” since {new Date(project.step_state.since).toLocaleDateString()}{project.nudge_settings?.by ? ` · last changed by ${project.nudge_settings.by.split('@')[0]}` : ''}</p>}
              <div className="flex justify-end">
                <Button size="sm" onClick={saveNudgeSettings} className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-resp-nudge-save-btn"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Save</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
            <DialogContent className="max-w-sm" data-testid="rfp-resp-reminder-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-sm">
                  <BellRing className="w-4 h-4 text-[#4ABFCF]" />Assignment reminders — {project.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-600">Remind assignees about unanswered questions</span>
                  <Switch checked={reminderForm.enabled} onCheckedChange={v => setReminderForm(f => ({ ...f, enabled: v }))}
                    data-testid="rfp-resp-reminder-switch" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">Remind after</span>
                  <Input type="number" min={1} max={30} value={reminderForm.days} disabled={!reminderForm.enabled}
                    onChange={e => setReminderForm(f => ({ ...f, days: Math.max(1, Math.min(30, parseInt(e.target.value || '1', 10))) }))}
                    className="h-8 w-20 text-xs" data-testid="rfp-resp-reminder-days-input" />
                  <span className="text-xs text-slate-600">day{reminderForm.days === 1 ? '' : 's'} (re-nudges at the same interval)</span>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={saveReminderSettings}
                    className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-resp-reminder-save-btn">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Save
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <AttachmentAdminDialog open={fileAdminOpen} onOpenChange={setFileAdminOpen} />
      <ReviewerWorkloadDialog open={reviewersOpen} onOpenChange={setReviewersOpen} assignees={assignees} onChanged={() => { if (project) openProject(project.id); }} />
      {project && <DocScaleDialog project={project} doc={scaleDoc} open={!!scaleDoc} onOpenChange={v => { if (!v) setScaleDoc(null); }} onChanged={() => openProject(project.id)} />}
      {project && <ScaleRemapDialog project={project} doc={remapDoc} open={!!remapDoc} onOpenChange={v => { if (!v) setRemapDoc(null); }} onChanged={() => openProject(project.id)} />}
      <NarrativePickerDialog open={!!narrTarget} onOpenChange={v => { if (!v) setNarrTarget(null); }} project={project} target={narrTarget}
        onInsert={async (text, mode) => {
          const doc = (project?.documents || []).find(d => d.id === narrTarget.docId);
          const cur = (doc?.questions || []).find(x => x.idx === narrTarget.idx)?.response || '';
          const next = mode === 'append' && cur.trim() ? `${cur.trim()}\n\n${text}` : text;
          await updateQuestion(narrTarget.docId, narrTarget.idx, { response: next, flag: 'flagged_for_review' });
          setNarrTarget(null);
          toast.success(mode === 'append' ? 'Paragraph appended — review and accept' : 'Narrative paragraph inserted — adapt it to this row, then accept');
        }} />
      <WinStoriesDialog open={winStoriesOpen}
        onOpenChange={v => { setWinStoriesOpen(v); if (!v) setInsertTarget(null); }}
        insertTarget={insertTarget}
        onInsert={async (story) => {
          await updateQuestion(insertTarget.docId, insertTarget.idx, { response: story.answer, flag: 'flagged_for_review' });
          setWinStoriesOpen(false);
          setInsertTarget(null);
          toast.success('Win story inserted — adapt it to this question, then accept');
        }} />
      <LibraryWordingDialog review={wordingReview} onCancel={() => setWordingReview(null)}
        onConfirm={(body, force) => (wordingReview.kind === 'promote' ? promoteToLibrary : addToLibrary)(wordingReview.docId, wordingReview.idx, { body, force })} />
    </div>
  );
}
